/**
 * The composer: one glass bar at the bottom centre of the canvas.
 *
 * What typed words MEAN depends on what is already on screen, and the box says
 * which: on a blank canvas they are the idea to explore, and plain ↵ explores
 * them; on an open graph they are a question about the selected node, or about
 * the graph, and plain ↵ asks it. Exactly two gestures say "these words are a
 * new claim" instead — a FRAME command (`/claim`, `/question`, `/goal`), which
 * is the typed door, and ⌘↵ / Ctrl+↵, which is the chord and the only door that
 * replaces an open graph without clearing it first.
 *
 * The rest it inherits from the old sidebar, because that contract was never
 * about the sidebar: /commands steer the next run and surface as removable chips
 * BEFORE anything is spent, and the primary button always states the thing it is
 * about to do — Ask, Explore, or Stop while anything is in flight.
 *
 * The tune panel above it holds everything that used to be rail furniture: lens,
 * temper, the channel switches, the run budget, and the spending ceiling — plus
 * the prose that states what a run will spend before it is pressed. Those
 * sentences moved here verbatim where they could: they are the app's honesty
 * about money, and a redesign does not get to soften them.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { CSSProperties, ReactNode } from 'react'
import {
  DEFAULT_EXECUTION,
  checkCanRead,
  connectedProviders,
  lockHeld,
  resolveFor,
  runAuditor,
  useActiveEntry,
  useStore,
} from '../core/store'
import type { RunTune, Theme } from '../core/store'
import {
  LENS_LABEL,
  OP_HINT,
  TEMPER_LABEL,
  channelOf,
  kindChannelOf,
  modelNames,
  untilLabel,
  type Lens,
  type RunVerb,
  type Session,
  type Temper,
} from '../core/types'
import {
  CROSS_EXAM_NEEDS_SECOND_KEY,
  PRICE_READ_ON,
  PROVIDERS,
  estimateSpendUsd,
  fmtUsd,
  looksLikeProviderKey,
} from '../core/models'
import { cacheFacts } from '../core/ledger'
import { cacheHealthOf, type CacheReading } from '../core/cacheHealth'
import { Icon } from './components/Icon'
import { useEscapeToClose } from './components/useEscapeToClose'
import {
  SEARCHES_PER_CHECK,
  SEARCHES_PER_PRIOR_ART,
  cancelCurrent,
  chainedRunDue,
  replicateKeyless,
  runSeed,
  type SeedOutcome,
  sharpenSeed,
} from '../engine/reason'
import type { SharpSeed } from '../engine/reason'
import { hasDemoReserve } from '../engine/demo'
import { STATUS_WORD } from '../core/vocabulary'
import { unreachableWhy } from '../core/failure'
import { pressTail } from './pressName'
import { liveLookupsPerCheck } from '../engine/liveSources'
import {
  budgetFor,
  ceilingFloors,
  closingScanUsd,
  continuationOf,
  specFromTune,
  rolesInPlay,
  scanIsReachable,
  projectMoveRange,
  projectModelPasses,
  projectRunPressUsd,
  projectRunUsd,
  projectVerbUsd,
  runModelsFor,
  startRun,
  type RunSpec,
} from '../engine/run'
import { dropHeldRun, holdRun, openingBegan, openingSettled, useOpening } from './runAfterOpening'
import { useCanCall } from '../core/store'
import { useVoiceOn } from './useVoiceOn'
import { useFigureBasis } from './useFigureBasis'
import {
  ACT_HELP,
  COMMAND_HELP,
  MOVE_ROWS,
  applySpec,
  parseAct,
  openQuestion,
  parseCommands,
  seedFrameReading,
  withoutDirectives,
  type ActKind,
} from '../engine/command'
import { offeredTune, suggestTune } from '../engine/suggest'
import { MAX_BRIEF, briefAddedToast, briefRefusal } from '../core/brief'
import { BriefList } from './BriefList'
import { anyStreaming, noteComposerDraft } from './askLive'
import { ACT_NAME, actAuditor, actPressName, actPriceVerb, actReady, takeAct } from './actPress'
import { runKeyGap } from './runGate'
import {
  committedChips,
  committedTune,
  resetPatch,
  type CommittedChip,
  type CommittedClear,
} from './steerChips'
import { THOUGHT_TINT } from './Overlay'
import { listDocs, loadCorpus, removeDoc, type CorpusDoc } from '../core/corpus'
import { glass } from './components/glass'
import { hoverFill, useHover } from './components/hover'
import { GlassShell } from './components/GlassShell'
import { SPEND_TITLE, VerbPrice } from './VerbPrice'
import { useAccount } from './ThreadFoot'
import { setAdjustOpen, useAdjustOpen } from './adjust'
import { RunStrip, type RunRest } from './RunStrip'
import { flushDurablePersistence } from '../core/autosave'
import { conversationOwnerTag, onConversationOwnerChanged } from '../core/conversations'
import { forgetExploration, setReopenGuard } from '../core/forget'
import { forgetLine } from './forgetPress'
import { ModePanel, ModeTabs, useNodeMode } from './NodeActs'
import { NextStepCard } from './NextStep'
import { setComposerMode } from './composerMode'

/** Same hints the old rail carried — one line on what each lens weighs. */
const LENS_HINT: Record<Lens, string> = {
  'first-principles': "The field's own constraints and definitions",
  technical: 'Tolerances, power, failure modes',
  empirical: 'What has actually been measured',
  economic: 'Cost, incentive, adoption',
  adversarial: 'Fastest route to disproof',
  systems: 'Couplings and second-order effects',
  historical: 'Precedent and prior attempts',
}

/** The temper's one line, in the shape `LENS_HINT` wears — and for the same
 *  reason: three near-identical names need their gloss at the point of choosing.
 *  `grounded` and `speculative` are the old chip rail's own words, lifted from
 *  the trailing hint at its foot, which read this table rather than repeating
 *  it; Adjust's temper dial reads it now. Only `balanced` had to be written,
 *  condensed from `TEMPER_BRIEF.balanced` — the brief itself is three sentences
 *  of instruction to the model, which is the wrong length for a menu row. */
const TEMPER_HINT: Record<Temper, string> = {
  grounded: 'Evidence only',
  balanced: 'Both, labelled by confidence',
  speculative: 'Speculation flagged',
}

/** Names from `TEMPER_LABEL`, shared with the scan panel — one dial, one vocabulary. */
const TEMPERS: Array<{ id: Temper; name: string }> = (
  ['grounded', 'balanced', 'speculative'] as const
).map((id) => ({ id, name: TEMPER_LABEL[id] }))

/** The cap presets, `null` for none. Four figures and a release — the typed
 *  amount below stays the custom case. */
const CAP_PRESETS: Array<number | null> = [null, 0.5, 2.5, 10, 50]

/**
 * What a cap buys, handed to the cap dial under Adjust by the composer that
 * computed it.
 *
 * The dial is where a cap is chosen — the Budget chip's popover, until the run
 * strip folded the four chips into one panel — and it used to be the one place
 * that said nothing about what a figure would do: two of its own presets sat
 * below the floor and silently disabled "Let it run", with the reason in a
 * tooltip that did not name the minimum. Every figure here is the tune panel's,
 * from the same calls, so the two surfaces that set a cap cannot describe it two
 * ways — nothing is typed, and the floor's model is whichever one set it
 * (`ceilingFloors`).
 */
export interface CapFacts {
  /** The pass reserve: the smallest cap a run can start under, and its model. */
  floor: { entry: { name: string }; usd: number; seats: number } | null
  /** What that floor bounds, in words — see `floorUnitOf`. */
  unit: string
  /** A source check's own reserve, when the run schedules any. */
  check: { entry: { name: string }; usd: number } | null
  moves: number
  /** The run's own projection, leaning high; null when unpriced. */
  est: number | null
  /** `projectMoveRange` over this budget, at an amount. */
  rangeAt: (usd: number) => { fewest: number; likely: number } | null
}

/** "7–12", or one number when the two ends meet. */
const rangeWords = (r: { fewest: number; likely: number }): string =>
  r.fewest === r.likely ? String(r.likely) : `${r.fewest}–${r.likely}`

/**
 * What a preset row says beside its figure. Below the floor it cannot start a
 * run, so it is greyed and names the figure it would take and the model that set
 * it; between the floor and a check's own reserve it runs, reasoning only, and
 * says that instead. Exported for its test, with the two sentences below.
 */
export function capPresetHint(
  v: number | null,
  f: CapFacts | undefined,
): { under: boolean; hint: string | null } {
  const floor = f?.floor ?? null
  if (v === null || floor === null) return { under: false, hint: null }
  if (v < floor.usd)
    return { under: true, hint: `from ${fmtUsd(floor.usd)} est. on ${floor.entry.name}` }
  return { under: false, hint: f?.check && v <= f.check.usd ? 'no source checks' : null }
}

/**
 * The floor, as the cap dial says it under its presets: the smallest cap and the
 * model that set it, then a check's own reserve when the run schedules checks.
 * The tune panel says the same two facts in its own place, off the same values.
 */
export function capFloorLine(f: CapFacts): string | null {
  if (!f.floor) return null
  const checks =
    f.check && f.check.usd > f.floor.usd
      ? ` A source check is held to ${fmtUsd(f.check.usd)}: with less left it is skipped, and the run keeps reasoning.`
      : ''
  return `From ${fmtUsd(f.floor.usd)} est. — the most ${f.unit} on ${f.floor.entry.name} could draw, held back before every pass so the cap is never crossed.${checks}`
}

/** What the cap now set would buy, in the tune panel's own words. */
export function capRangeLine(f: CapFacts, usd: number): string | null {
  if (!f.floor) return null
  if (usd < f.floor.usd)
    return `${fmtUsd(usd)} cannot be guaranteed to cover ${f.unit} on ${f.floor.entry.name}, so the run would stop before its first move.`
  if (f.est !== null && usd >= f.est)
    return `Above what ${f.moves} moves could draw, so the move count goes first and the cap is a backstop.`
  const r = f.rangeAt(usd)
  const noChecks = f.check !== null && usd <= f.check.usd
  return r
    ? `You'll likely get ${rangeWords(r)} of the ${f.moves} moves${noChecks ? ', and no source checks' : ''} — projected from list prices, not promised.`
    : null
}

/** The cap stepper's arithmetic, shared by the tune panel and the cap dial —
 *  two steppers with two roundings would drift by a cent and argue. The
 *  increment scales with magnitude: quarters below $1, tens above $20, clamped
 *  to $0.25…$200. From "no ceiling" a first press up lands on the smallest cap. */
function stepCapAmount(cur: string, dir: 1 | -1): string {
  const n = Number.parseFloat(cur)
  const base = Number.isFinite(n) && n > 0 ? n : 0
  const inc = base < 1 ? 0.25 : base < 5 ? 0.5 : base < 20 ? 2.5 : 10
  return String(Math.round(Math.min(200, Math.max(0.25, base + dir * inc)) * 100) / 100)
}

/** Tokens, never rounded to zero — see the old rail for why. */
const tok = (n: number): string => (n < 1000 ? String(n) : `${(n / 1000).toFixed(1)}k`)

/** Digits and at most one point, two decimals — filtered on the way in. */
function cleanAmount(raw: string): string {
  const kept = raw.replace(/[^\d.]/g, '')
  const [whole = '', ...rest] = kept.split('.')
  const cents = rest.join('').slice(0, 2)
  return `${whole.slice(0, 6)}${rest.length ? `.${cents}` : ''}`
}

/** A tab row's options with the value in force always among them. */
const withCurrent = (offered: number[], current: number): number[] =>
  offered.includes(current) ? offered : [...offered, current].sort((a, b) => a - b)

/**
 * Whether this exploration's prompt cache is holding, under the per-model counts
 * that already say how much of the input came from it.
 *
 * The counts alone cannot answer the question a reader actually has. A low share
 * has two opposite causes — a prefix that keeps changing, and a window that keeps
 * expiring — and they call for opposite responses, so a percentage with nothing
 * beside it invites the wrong one. `classifyCache` separates them on the GAP, and
 * the sentence it built rides in the title rather than on screen, like every other
 * explanation in this panel.
 *
 * Renders nothing when there is nothing to classify, exactly as the cache line
 * above renders nothing on a row with no breakdown. Silence here is not a claim
 * that the cache is healthy — it is the same silence as a session that has not
 * made two passes on one model yet.
 */
function CacheReadingLine({ session }: { session: Session }) {
  const health = cacheHealthOf(session)
  if (health.reading === 'not-evidenced') return null
  const said: Record<Exclude<CacheReading, 'not-evidenced'>, string> = {
    healthy: 'prompt cache holding between passes',
    'expiry-bound': 'prompt cache expiring between passes',
    'prefix-unstable': 'prompt cache rewritten between passes',
  }
  return (
    <p className="num" style={{ ...NOTE, textAlign: 'center' }} title={health.rule}>
      {said[health.reading]}
    </p>
  )
}

const NOTE: CSSProperties = {
  fontSize: 'var(--fs-micro)',
  lineHeight: 1.5,
  color: 'var(--text-3)',
  textWrap: 'pretty',
}
const EYEBROW: CSSProperties = {
  fontSize: 'var(--fs-micro)',
  fontWeight: 590,
  letterSpacing: 'var(--tracking-caps)',
  textTransform: 'uppercase',
  color: 'var(--text-3)',
}

/**
 * The draft, surviving the composer changing homes. The bar has two mounts —
 * the thread's foot and the floating strip — and which one renders flips on a
 * node tap, on ⌘], and on a seed landing (`composerInPane` in App), so
 * component state alone dies at exactly the wrong moment: half a typed
 * question, lost to the very tap that names its subject. Module scope rather
 * than the store — the same StrictMode-proof latch App's `bootstrapped` uses,
 * and the store's frozen contract (D36) stays frozen. Keyed to the session it
 * was typed against, so a draft can never follow the reader into a different
 * exploration opened while the bar was unmounted.
 */
let savedDraft = ''
let savedDraftSession = ''
/**
 * Focus and caret ride the latch too. A draft restored without them still
 * strands the typist: ⌘] is not gated on typing (deliberately — the bracket
 * must work everywhere), so it unmounts the very input the caret is in, focus
 * falls to the document, and the next keystroke lands nowhere.
 *
 * Kept live from the input's own events rather than captured at unmount:
 * passive cleanup runs after React has detached the ref, so an unmount
 * capture reads nothing. A REAL blur clears it, and that is a behaviour, not
 * a concession — no blur fires when a focused element is removed, so a home
 * flip keeps the caret, while a canvas tap (which blurs first) does not: the
 * reader who just selected a node wants D to Deepen it, not to type a letter
 * into a box that grabbed focus back. Restored on a desktop only — the
 * phone's remount is the sheet closing, where an auto-focus would raise the
 * soft keyboard over a dismissal.
 */
let savedCaret: { start: number; end: number } | null = null
/**
 * A change of reader empties the latch, and a question handed to the feed and
 * not yet taken goes with it. The latch is keyed to the exploration and not to
 * who typed into it, so on a shared workspace one account's half-written
 * question was restored into the next account's box on the same graph — and a
 * handoff nobody had taken yet would be asked, and billed, as the next reader's
 * (A01). Module scope, because the latch outlives the box: no composer need be
 * mounted when somebody signs out. A mounted one clears its own state below.
 */
/**
 * A refused Forget reopens the exploration it cleared (`core/forget.ts`) — but not
 * over words the reader has started typing on the cleared canvas, which the
 * reopen would empty from this box with the change of exploration. Asked of the
 * latch, by session: words typed against that canvas, whether or not a box is
 * mounted to show them. The box's own flag outlives the box, and would answer
 * with the words of an exploration that has already gone (D331).
 */
setReopenGuard((sessionId) => savedDraftSession === sessionId && savedDraft.trim().length > 0)
onConversationOwnerChanged(() => {
  savedDraft = ''
  savedCaret = null
  useStore.getState().proposeQuestion(null)
  // The other handoff into this box — a seed given back, or pressed in from the
  // palette or the sources sheet — is words somebody chose too.
  useStore.getState().proposeSeed(null)
})
/**
 * The last `sigCaret` a box took. Module-level for the reason the draft latch
 * is: the box that should take the caret is often a NEW instance — a new
 * exploration empties the canvas, which moves the composer out of the thread's
 * column and into the field — and a ref would start every instance at zero
 * and take a signal already spent.
 */
let caretTaken = 0

/**
 * An opening refused for its KEY gives the question back.
 *
 * The box clears the moment a seed is sent, and a mistyped key surfaces only
 * there, on the first paid pass: the reader was taken to the key field
 * (`askForKey`) and came back to a canvas holding one bare seed and an empty box —
 * the question they had typed, gone on the detour. So when the opening produced
 * nothing, billed nothing and failed on the key, the lone seed is put down —
 * `newExploration`, and then the stored copy through the Forget every door shares
 * (`forgetExploration`, D331) — and the words go back in the box exactly as typed,
 * commands and all, without waiting for the shelf. One Enter after the key is
 * fixed asks it again, with no second row of the same seed left in Recent; a
 * deletion the shelf refuses leaves that row, and says so. Anything else — a pass
 * that landed nodes or billed, a different failure, an exploration the reader has
 * since left — is left alone.
 *
 * The seed's last write is awaited FIRST. A forget under a pending write of the
 * same document reads, to the durable layer, as a forget from another tab, and
 * pauses paid work; flushed, the switch queues nothing for it (`queueState` skips
 * a session already queued). If the save fails, or the seed changed meanwhile,
 * its row is kept — the safe side — and the words still come back.
 */
async function takeBackRefusedSeed(
  out: SeedOutcome,
  words: string,
  /** Who pressed, taken at the press (`conversationOwnerTag`). */
  reader: string,
): Promise<boolean> {
  // The words go back to the reader who typed them or to nobody. An opening
  // can fail long after its press, and a reader who changed meanwhile would be
  // handed the last one's question — and have their own graph put down for it.
  if (conversationOwnerTag() !== reader) return false
  if (out.failure !== 'auth' || out.produced > 0) return false
  const refused = useStore.getState().session
  if (refused.id !== out.sessionId || refused.order.length > 1 || refused.spend.length > 0)
    return false
  let flushed = true
  try {
    await flushDurablePersistence()
  } catch {
    flushed = false
  }
  const s = useStore.getState()
  if (s.session.id !== refused.id) return false
  if (conversationOwnerTag() !== reader) return false
  const untouched = flushed && s.session === refused
  if (!s.newExploration()) return false
  useStore.getState().proposeSeed(words)
  if (untouched)
    void forgetExploration(refused.id).then((gone) => {
      // Silent when it went, as it always was: the words are back and the key
      // field is open, and that is all this moment needs to say. A refusal
      // leaves a row behind, which is worth one sentence.
      if (gone.kind !== 'kept') return
      const line = forgetLine(gone)
      if (line) useStore.getState().toast(line.text, line.tone)
    })
  return true
}

export function Composer({
  docked = false,
}: {
  /** Standing in the thread's column, as against floating over the field — the
   *  blank canvas, a desktop with the thread closed, and the phone (D353). */
  docked?: boolean
} = {}) {
  const theme = useStore((s) => s.theme)
  const session = useStore((s) => s.session)
  const rev = useStore((s) => s.rev)
  const busy = useStore((s) => s.busy)
  // `busy` less the worked example's unfurl, which a seed stops rather than
  // waits for (`runSeed`) — so the box offers to seed over it, and does not
  // put Stop where the press was. The unfurl keeps its Stop on the thought line.
  const locked = useStore(lockHeld)
  const runLive = useStore((s) => s.run !== null && s.run.phase !== 'ended')
  // Whether the strip is showing an ending's head, whose cause line already says
  // why a run the API stopped answering stopped (D337).
  const endingShown = useStore((s) => s.run !== null && s.run.phase === 'ended')
  const modelId = useStore((s) => s.modelId)
  const roleModels = useStore((s) => s.roleModels)
  const selectedId = useStore((s) => s.selectedId)
  const toast = useStore((s) => s.toast)
  const setSources = useStore((s) => s.setSources)
  const sourcesOpen = useStore((s) => s.sourcesOpen)
  const askAbout = useStore((s) => s.askAbout)
  const proposeQuestion = useStore((s) => s.proposeQuestion)
  const tuneOpen = useStore((s) => s.tuneOpen)
  const runTune = useStore((s) => s.runTune)
  const setRunTune = useStore((s) => s.setRunTune)
  const runCeiling = useStore((s) => s.runCeiling)
  const setRunCeiling = useStore((s) => s.setRunCeiling)
  const tuneDefaults = useStore((s) => s.tuneDefaults)
  const setBrief = useStore((s) => s.setBrief)
  // For the tune suggestion below — the note compares against these, and its
  // press writes them through the same setters Adjust's dials call.
  const lens = useStore((s) => s.lens)
  const setLens = useStore((s) => s.setLens)
  const temper = useStore((s) => s.temper)
  const setTemper = useStore((s) => s.setTemper)

  const [draft, setDraft] = useState(() => (savedDraftSession === session.id ? savedDraft : ''))
  // The ✕ on the pre-filled question chip, for the question it was pressed on.
  // A draft that stops being an open question — emptied, or rewritten — forgets
  // it, so the next one is read as a question again.
  const [questionDeclined, setQuestionDeclined] = useState(false)
  /**
   * Sharper versions of the words in the box, from one cheap pass, and never
   * applied on their own. Cleared whenever the box changes, because a candidate
   * offered about earlier words is a candidate about something else — the
   * `promotableTurn` rule, applied to a draft.
   */
  const [sharp, setSharp] = useState<SharpSeed[]>([])
  const [sharpening, setSharpening] = useState(false)
  // The latch's write half, declared before the new-session clear below so a
  // session change ends with the module copy holding the cleared box rather
  // than the words typed against the graph it replaced.
  useEffect(() => {
    savedDraft = draft
    savedDraftSession = session.id
    // The feed's "Explain simply" stands down while the box holds a question.
    noteComposerDraft(draft)
  }, [draft, session.id])
  useEffect(() => setSharp([]), [draft, session.id])
  const [focused, setFocused] = useState(false)
  const [srcOpen, setSrcOpen] = useState(false)
  const [srcAnchor, setSrcAnchor] = useState<{ left: number; up: number } | null>(null)
  const [runHov, runBind] = useHover()
  const inputRef = useRef<HTMLInputElement | null>(null)
  // A run pressed on the strip while the opening is going waits for it (D351).
  const { opening, held } = useOpening()
  const account = useAccount()

  /** The caret latch's write half, from whichever input event moved it. */
  const captureCaret = (el: HTMLInputElement) => {
    savedCaret = {
      start: el.selectionStart ?? el.value.length,
      end: el.selectionEnd ?? el.value.length,
    }
  }
  // The restore half: a caret on record means the last unmount was a home
  // flip, not a blur — seat focus back so typing continues mid-sentence. Same
  // session only, desktop only, and the range clamps itself where a clear
  // shortened the value.
  useEffect(() => {
    const el = inputRef.current
    if (
      savedCaret !== null &&
      savedDraftSession === useStore.getState().session.id &&
      !window.matchMedia('(max-width: 767px)').matches &&
      el
    ) {
      const { start, end } = savedCaret
      el.focus()
      el.setSelectionRange(start, end)
    }
  }, [])

  // A new session is a new box. The seed of record lives in the capsule and on
  // the canvas; the composer is a prompt bar, not a document field. On the
  // CHANGE of id, not the value
  // (the `sigRewrite` precedent): this effect also fires on mount, which is now
  // a home switch — clearing there would wipe the very draft the latch above
  // just restored. The module copy is cleared alongside, so an unmount racing
  // this flush cannot revive the old session's words.
  const seenSession = useRef(session.id)
  useEffect(() => {
    if (session.id === seenSession.current) return
    seenSession.current = session.id
    savedDraft = ''
    savedDraftSession = session.id
    setDraft('')
    // An armed act names a node of the graph this change just left; firing it
    // against the new one would spend on a claim nobody pressed.
    setPendingAct(null)
  }, [session.id])
  // A new READER is a new box too, on the same graph or not: the words in it
  // are the last reader's. See the latch's own subscription above, which covers
  // the box that is not mounted. An armed act is the last reader's press, and
  // so is a run held for the opening (D351).
  useEffect(
    () =>
      onConversationOwnerChanged(() => {
        setDraft('')
        setSharp([])
        dropHeldRun()
        setPendingAct(null)
      }),
    [],
  )

  // The caret, handed over by a door that leaves somebody about to type (see
  // `sigCaret`). Taken once, by whichever box is mounted when it lands, and on
  // desktop only. It runs after a closing sheet's own focus restore — React
  // cleans up every passive effect in a commit before it runs any — so a key
  // saved in the setup sheet ends with the caret here, not on the gear.
  const sigCaret = useStore((s) => s.sigCaret)
  useEffect(() => {
    if (sigCaret === caretTaken) return
    caretTaken = sigCaret
    if (window.matchMedia('(max-width: 767px)').matches) return
    // The box is Ask's, and hidden on the other modes: the caret takes Ask
    // with it, and the box after the render that shows it (D347).
    setComposerMode('ask')
    requestAnimationFrame(() => inputRef.current?.focus())
  }, [sigCaret])

  // A seed offered from elsewhere (the corpus dialog). One-shot, like the old
  // rail's seed card it replaces: taken and cleared together.
  const proposedSeed = useStore((s) => s.proposedSeed)
  const proposeSeed = useStore((s) => s.proposeSeed)
  useEffect(() => {
    if (proposedSeed === null) return
    setDraft(proposedSeed)
    proposeSeed(null)
  }, [proposedSeed, proposeSeed])

  // The A key's other half. `askAbout` used to open a panel with its own box;
  // the feed's box is this composer, so the pin's ARRIVAL seats the caret here
  // — on the transition, not the value, or every re-render with a pin held
  // would steal focus from whatever the reader was doing. Desktop only: on the
  // phone a programmatic focus raises the keyboard over the sheet the tap just
  // opened. A bridge pin is Breakthroughs' own conversation, with its own box.
  const chatPin = useStore((s) => s.chat)
  const seenPin = useRef(chatPin)
  useEffect(() => {
    const was = seenPin.current
    seenPin.current = chatPin
    if (
      chatPin !== null &&
      chatPin !== was &&
      chatPin.kind !== 'bridge' &&
      !window.matchMedia('(max-width: 767px)').matches
    ) {
      setComposerMode('ask')
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [chatPin])

  const parsed = useMemo(() => parseCommands(draft), [draft])
  useEffect(() => {
    if (!openQuestion(parsed.seed)) setQuestionDeclined(false)
  }, [parsed.seed])
  const canExplore = parsed.seed.length > 3 && !locked
  // The sharpen offer is a model pass like any other, so it reads whether one
  // can be called at all — the same question its own press asks.
  const canCallModel = useCanCall()
  /** A seed's directives past the brief's cap, or past a directive's length:
   *  refused at the press and said above the bar, never cut on the way to disk
   *  after every pass of the session had been sent them (`briefRefusal`). */
  const seedBriefHold = parsed.spec.directives?.length
    ? briefRefusal([], parsed.spec.directives)
    : null
  // Declared here rather than beside `askContext` below, because the
  // completion list is the first reader: the typed-act entries only make sense
  // over a graph with a selection to act on.
  const hasGraph = session.order.length > 0

  /** The sheet's Row 1: the selected node's dot and title above the input. */
  const selNode = selectedId ? (session.nodes[selectedId] ?? null) : null
  // The composer's modes (D347): with a node selected over an open graph, the
  // acts the card used to carry, one mode at a time; the box is Ask's.
  const modeNode = hasGraph ? selNode : null
  const mode = useNodeMode(modeNode)
  const modeId = useId()
  /** How many private sources are attached — re-read when the dialog closes,
   *  which is the only place the count changes. */
  const corpusCount = useMemo(
    () => loadCorpus().length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sourcesOpen, rev],
  )

  const typing = /(?:^|\s)\/([a-z]*)$/i.exec(draft)
  // The acts lead the run commands: over an open graph a bare `/d…` is more
  // often reaching for Deepen-now than for a run dial, and on a blank canvas
  // the acts are not offered at all — there is no selection for them to act on.
  const completions = typing
    ? (hasGraph ? [...ACT_HELP, ...COMMAND_HELP] : COMMAND_HELP)
        .filter((h) => h.name.startsWith(typing[1]!.toLowerCase()))
        .slice(0, 6)
    : []
  /**
   * Which completion the arrows have walked to, held as the command's NAME
   * rather than its row number.
   *
   * The list re-filters on every keystroke, so an index is a pointer into a
   * table that has already changed underneath it — walk to row 2 of `/d`, type
   * `i`, and row 2 of `/di` is a different command with ↵ aimed at it. A name
   * either survives the narrowing, in which case the highlight rightly follows
   * it down, or it does not and `cmpAt` reads -1 with nothing to reset.
   *
   * It starts at NOTHING, and that is what keeps ↵ meaning what it always
   * meant: with nothing walked to, Enter submits the box exactly as it did
   * before this list learned the keyboard. Arming the first row on open would
   * turn every `/de` ↵ into a completion nobody asked for.
   */
  const [cmpName, setCmpName] = useState<string | null>(null)
  const cmpAt = completions.findIndex((h) => h.name === cmpName)
  const walkCompletions = (step: 1 | -1) => {
    // "Nothing" is a rung on the cycle, not a floor: walking off either end
    // hands the box back to the user's own words rather than trapping the
    // highlight in a list they were only glancing at.
    const n = completions.length
    const at = ((cmpAt + 1 + step + (n + 1)) % (n + 1)) - 1
    setCmpName(at < 0 ? null : completions[at]!.name)
  }
  const complete = (name: string) => {
    setDraft(`${draft.slice(0, draft.length - typing![1]!.length)}${name} `)
    // The walked-to row is spent by the take, whether a click or ↵ took it.
    // Left standing, it would re-arm itself the next time a /token happened to
    // surface the same command — and ↵ would then fire a completion the user
    // chose several words ago.
    setCmpName(null)
  }
  /** The completion list's id, referenced only while it is in the document —
   *  see `Field` in components/Controls.tsx for the same rule stated once. */
  const ids = useId()
  const cmpListId = `${ids}-commands`
  const cmpRowId = (name: string) => `${ids}-command-${name}`
  /** The walked-to row's id, or nothing while the highlight is on the user's own
   *  words. One value for the two readers that must agree on it: the input's
   *  `aria-activedescendant`, and the scroll below. */
  const cmpActiveId = cmpAt >= 0 ? cmpRowId(completions[cmpAt]!.name) : undefined
  // The rows above the bar are a scroll container now (`.cabove`), so in a pane
  // short enough to clip the list the arrows could walk the highlight below its
  // fold — and ↵ would then complete an option nobody could see. Keep the
  // walked-to row in the frame. `nearest` moves nothing when it already is,
  // which is every case over the field, where the column never scrolls.
  useEffect(() => {
    if (cmpActiveId === undefined) return
    document.getElementById(cmpActiveId)?.scrollIntoView({ block: 'nearest' })
  }, [cmpActiveId])

  const dropChip = (source: string) => {
    const at = draft.indexOf(source)
    if (at < 0) return
    setDraft(
      `${draft.slice(0, at)}${draft.slice(at + source.length)}`
        .replace(/[ \t]{2,}/g, ' ')
        .trimStart(),
    )
  }

  /** The one answer to "what will the next run do" — commands win over controls. */
  const live = useMemo(
    () => ({
      ...applySpec(
        {
          moves: runTune.moves,
          checks: runTune.checks,
          panels: runTune.panels,
          audits: runTune.audits,
          ...(runTune.conjectures !== undefined ? { conjectures: runTune.conjectures } : {}),
          ceiling: runCeiling,
          ladder: runTune.ladder,
          depth: runTune.depth,
          until: runTune.until,
          mode: runTune.mode,
        },
        parsed.spec,
      ),
      execution: runTune.execution ?? DEFAULT_EXECUTION,
      detail: runTune.detail ?? 'full',
    }),
    [runTune, runCeiling, parsed.spec],
  )

  const keys = useStore((s) => s.keys)
  const customBase = useStore((s) => s.customBase)
  const customModel = useStore((s) => s.customModel)
  // The house a run's cross-examinations and /replicate's second opening would
  // go to: the one reasoning does NOT run on (`runAuditor`), as `startRun`,
  // `runKeyGap` and `runSeed` resolve it. The selection's was read here, so with
  // reasoning routed elsewhere the projection priced — and the refusals below
  // judged — the house a run would audit WITH.
  const auditor = useMemo(
    () => runAuditor({ modelId, roleModels, keys, customBase, customModel }),
    [modelId, roleModels, keys, customBase, customModel],
  )

  // Through `specFromTune`, which is now the one place the dials become a spec.
  // `focus` stays here because it names this composer's selection rather than
  // anything the dials hold.
  const spec: RunSpec = useMemo(
    () => ({
      ...specFromTune(live),
      ...(parsed.spec.focus && selectedId ? { focus: selectedId } : {}),
    }),
    [live, parsed.spec.focus, selectedId],
  )
  const budget = useMemo(() => budgetFor(spec), [spec])
  const runModels = useMemo(() => runModelsFor({ modelId, roleModels }), [modelId, roleModels])
  // The scan is the one role a run cannot turn off, so it is in every figure but
  // one: a budget allowing fewer than `SCAN_FLOOR` nodes cannot reach it. A
  // property of the budget and not of the graph — a run counts its own nodes
  // (D74) — so a run held for an opening, on a graph that does not exist yet,
  // asks the same question and gets the same answer (D351). `startRun` conditions its
  // refusals and its reserve the same way, and a sidebar floor above the one the
  // run enforces would refuse a ceiling the run would have taken — the field
  // would be arguing with the button.
  const scanInPlay = useMemo(() => scanIsReachable(budget), [budget])
  const roles = useMemo(() => rolesInPlay(budget, scanInPlay), [budget, scanInPlay])
  // The closing scan as its own prompt prices it over the graph as it stands —
  // what its gate will print — which the projection takes at no less than a
  // move (`scanSlotUsd` in `engine/run.ts`): the scan reads the graph the run
  // leaves, and that graph is only larger.
  const scanUsd = useMemo(
    () => closingScanUsd({ modelId, roleModels, lens, temper, session }),
    [modelId, roleModels, lens, temper, session],
  )
  /** The moves alone — what a ceiling is compared against and divided into
   *  moves. The voice's summary yields under a ceiling, so it is not in here. */
  const runMovesEst = projectRunUsd(
    runModels,
    budget,
    auditor?.entry ?? null,
    scanInPlay,
    scanUsd,
    spec.execution,
  )
  // Heard so a figure that includes the voice moves when its switch does.
  const voice = useVoiceOn()
  useFigureBasis()
  /** What pressing the run is quoted at: the moves, and the voice's one
   *  wrap-up when the voice is on (D295). Every figure printed before the press. */
  const runEst = projectRunPressUsd(
    { modelId, roleModels },
    runModels,
    budget,
    auditor?.entry ?? null,
    scanInPlay,
    scanUsd,
    spec.execution,
  )
  /**
   * Whether this run has the keys it needs — the run press's gate, asked of the
   * ROUTES its passes run on, never of the selection's key (`runKeyGap`): with
   * the check routed to a company whose key is not stored, the strip priced a
   * run `startRun` would refuse, and with the selection keyless under a keyed
   * route it greyed a run that would have started. A run whose dials schedule
   * checks is not ready without the check's key (invariant 4 — they would not
   * fall back to the selection), and one that schedules none is. Its sentence
   * is the press's own refusal.
   */
  const keyGap = useMemo(
    () =>
      runKeyGap({ modelId, roleModels, keys, customBase, customModel }, roles, spec.audits ?? 0),
    [modelId, roleModels, keys, customBase, customModel, roles, spec.audits],
  )
  /** Whether the reader holds any key: with none, "Needs a key" is the whole
   *  story; with one, it reads as "but I have one", so the strip says which. */
  const keyHeld = connectedProviders({ keys, customBase }).length > 0
  const auditorInPlay = (budget.audits ?? 0) >= 1 ? (auditor?.entry ?? null) : null
  /**
   * The floor AND the model that set it, from one walk.
   *
   * `dearestPass` rather than `minCeilingForRun`, which returns the number
   * alone: this figure is the max over reason, check, panel, the auditor and a
   * reachable scan, so the model it came from is often not the selection. The
   * warning below used to say the ceiling could not cover a pass on the SELECTED
   * model while a routed one had set the floor — which is the exact bug
   * `dearestPass` was introduced for on `startRun`'s side, left standing here
   * because I fixed the refusal and not the sentence beside it.
   *
   * Per role, through `ceilingFloors`, which is `dearestPass` over every role but
   * the check plus a check's own bound: the floor a run can START under is the
   * pass reserve, and a cap between it and the check's runs reasoning and skips
   * its checks. Read from the one function `startRun` enforces, so the panel
   * cannot refuse a cap the run would take, or take one it would refuse.
   */
  const floors = useMemo(
    () => ceilingFloors(runModels, auditorInPlay, roles),
    [runModels, auditorInPlay, roles],
  )
  const ceilingDearest = floors?.pass ?? null
  const checkFloor = floors?.check ?? null
  const ceilingUsd = live.ceiling ? Number(live.ceiling) : null
  const ceilingSet = ceilingUsd !== null && Number.isFinite(ceilingUsd) && ceilingUsd > 0
  const ceilingTooLow =
    ceilingSet && !!ceilingDearest && (ceilingUsd as number) < ceilingDearest.usd
  const moveRange = useMemo(
    () =>
      ceilingSet && !ceilingTooLow && ceilingUsd !== null
        ? projectMoveRange(
            runModels,
            budget,
            auditor?.entry ?? null,
            ceilingUsd,
            scanInPlay,
            true,
            scanUsd,
            spec.execution,
          )
        : null,
    [
      ceilingSet,
      ceilingTooLow,
      ceilingUsd,
      runModels,
      budget,
      auditor,
      scanInPlay,
      scanUsd,
      spec.execution,
    ],
  )

  const nodeTotal = Math.max(0, session.order.length - 1)
  /**
   * An open question on a blank canvas is READ as one before Enter, under the
   * same "read as a question" chip `/question` makes (D123). Visible and
   * reversible before anything is spent: its ✕ reads the draft as a claim, for
   * as long as this draft lasts. Only where the words will seed — over an open
   * graph they are a question FOR Ask, and a frame there would replace the graph.
   */
  const reading = seedFrameReading({
    seed: parsed.seed,
    typed: parsed.spec.frame,
    current: session.frame,
    overGraph: hasGraph,
    declined: questionDeclined,
  })
  const autoQuestion = reading.prefilled
  const nextFrame = reading.frame
  const suggestion = reading.suggestion

  /**
   * The dials' half of the same reading: the lens, temper and move budget the
   * words look like they want (`engine/suggest.ts`). `offeredTune` holds the
   * offering rules — a satisfied dial says nothing, a typed `/moves` silences
   * the moves cue outright — as a pure function, so they are pinned by its
   * tests rather than living unpinned in this JSX.
   */
  const tuneCues = useMemo(
    () =>
      offeredTune(suggestTune(parsed.seed), {
        lens,
        temper,
        moves: runTune.moves,
        commandMoves: parsed.spec.moves,
      }),
    [parsed.seed, parsed.spec.moves, lens, temper, runTune.moves],
  )

  /** The press that takes the reading. It writes the same store fields Adjust's
   *  dials write, so the run strip and its dials show what it set and any of it
   *  can be changed back one dial at a time. Only ever a press: the moment a
   *  good reading applies itself, a bad one does too, and the person has no
   *  idea which they got (D35's rule, and `suggestFrame`'s). */
  const applyTune = () => {
    for (const c of tuneCues) {
      if (c.dial === 'lens') setLens(c.lens)
      else if (c.dial === 'temper') setTemper(c.temper)
      else setRunTune({ ...runTune, moves: c.moves })
    }
  }

  /**
   * Whether the opening would be refused for its key, and if so the refusal: the
   * reasoning role's house, which `runOp` resolves the opening on — not the
   * selection's, which under routing can hold a key while the opening has none.
   * Asked BEFORE the box is cleared, so the words are still in it after the
   * detour to that key's field. Refused past this point, by the pass itself, they
   * were left on a lone seed and the box was empty.
   */
  const openingKeyless = (): boolean => {
    if (resolveFor(useStore.getState(), 'reason')) return false
    useStore.getState().askForMissingKey('reason', 'Exploring an idea')
    return true
  }
  /** …and `/replicate`'s second opening, which needs another company's key, asked
   *  at the same moment for the same reason: refused after the first opening,
   *  that opening was spent on half a comparison. */
  const seedKeyless = (): boolean =>
    openingKeyless() || (parsed.spec.replicate === true && replicateKeyless())

  const explore = () => {
    if (!canExplore) return
    if (seedBriefHold) {
      toast(seedBriefHold, 'warn')
      return
    }
    // ⌘↵ replaces the open graph, and it is the one new-exploration door that
    // never consulted the shared lock: `canExplore` checks `!locked`, and `busy` is
    // false BETWEEN a live run's passes. The run loop absorbs the replacement
    // cleanly as stop reason 'replaced', but ending a paid run with no word said
    // is not something a chord should be able to do. Same sentence as the door.
    if (hasGraph && runLive) {
      toast('One thought at a time — let the current branch finish.', 'warn')
      return
    }
    if (seedKeyless()) return
    // Committed BEFORE the box is cleared, or a chip's promise dies with it —
    // and through `committedTune`, so a field cannot fall out of this write
    // while the chips still claim it committed.
    setRunTune(committedTune(live))
    setRunCeiling(live.ceiling)
    const words = draft
    const reader = conversationOwnerTag()
    // While it is going, the strip's Run can be pressed and is held for it (D351).
    openingBegan()
    void runSeed(parsed.seed, nextFrame, parsed.spec.directives, {
      replicate: parsed.spec.replicate,
    }).then(
      (out) => afterOpening(out, words, reader),
      () => void openingSettled(),
    )
    setDraft('')
  }

  /**
   * The opening has landed, failed or been stopped: the words back where a key
   * refused it, and the run the strip held for it started — only where the
   * opening landed clean on the exploration it seeded (`chainedRunDue`), and
   * through `startRun`, so every refusal still refuses out loud. The two skips
   * that would otherwise pass in silence say so; a failure has its own toast
   * already, and a Stop is the reader's hand, honoured without a word.
   */
  const afterOpening = async (out: SeedOutcome, words: string, reader: string) => {
    const run = openingSettled()
    if (await takeBackRefusedSeed(out, words, reader)) return
    if (!run) return
    // The run starts only on the graph this press seeded. Opening a saved
    // exploration takes no lock, so the session now may be one the reader opened
    // meanwhile to READ — their navigation is their hand, honoured as a stop is.
    if (useStore.getState().session.id !== out.sessionId) return
    if (!chainedRunDue(out.produced, out.failure)) {
      if (out.produced === 0 && out.failure === null)
        toast('The opening landed nothing to run on, so the run was not started.', 'warn')
      else if (out.produced > 0 && out.failure !== null && out.failure !== 'aborted')
        toast(
          'The opening reported a failure, so the run was not started — Run, above the input, when the graph looks right.',
          'warn',
        )
      return
    }
    void startRun(run.spec)
  }

  /** The run the dials describe, as the strip priced it. */
  const runSpecNow = (): RunSpec => ({
    ...spec,
    usdMax: ceilingSet ? (ceilingUsd as number) : null,
  })
  const letItRun = () => {
    // Under the opening the press is held until it lands, at the figure the strip
    // showed (D351). Every other gate is the press's own.
    if (opening && !runLive) {
      if (held || keyGap || ceilingTooLow) return
      holdRun({ spec: runSpecNow(), moves: budget.moves, est: runEst })
      return
    }
    if (busy || runLive || keyGap || nodeTotal < 1 || ceilingTooLow) return
    void startRun(runSpecNow())
  }

  /**
   * What typed words MEAN depends on where you are standing. On an empty canvas
   * they are the idea to explore. On an existing graph they are a question about
   * it — pinned to the selected node, or to the whole graph — UNLESS a FRAME
   * command says outright that they are a new claim, or ⌘↵ does. A question
   * takes no lock, so it works even while a pass is thinking.
   */
  // Only the FRAME family makes these words a new claim. Any other recognised
  // /command used to flip this, so plain unmodified Enter on an open graph would
  // seed a new exploration and destroy the current one — while the box said
  // "Ask about this graph". `/claim`, `/question` and `/goal` share one chip via
  // `chipKey: 'frame'`, and command.ts writes that key into the chip's `name`.
  const commandy = parsed.chips.some((c) => c.name === 'frame')
  const askContext = hasGraph && !commandy
  // A question is what REMAINS once the commands are stripped, not the raw box.
  // Narrowing `commandy` to the frame family above made this path reachable with
  // a /command in the box for the first time — and a draft that is nothing but
  // `/moves 40` has no question in it. Measured on the raw draft it would light
  // the primary button, post the command's own text to the model as if it were
  // prose, and bill for the call.
  const askable = parsed.seed.trim().length > 1

  const doAsk = () => {
    const q = parsed.seed.trim()
    if (q.length < 2) return
    // The role an Ask resolves to, a routed one where routed: its house's card
    // opens, and the question stays in the box for when the reader comes back.
    if (!resolveFor(useStore.getState(), 'chat')) {
      useStore.getState().askForMissingKey('chat')
      return
    }
    // A handoff the feed cannot take should stay in the box (D37): the send
    // would be refused while another answer is arriving, and the box will have
    // already cleared itself. Asked HERE, where the words still are.
    if (anyStreaming()) {
      toast('One thought at a time — another answer is still arriving.', 'warn')
      return
    }
    askAbout(selectedId ? { kind: 'node', id: selectedId } : { kind: 'graph' })
    proposeQuestion(q)
    setDraft('')
  }

  /**
   * A typed act: `/verify`, `/deepen`, … — the node card's own presses, from
   * the box. The DRAFT is only ever a request to arm: a paid act lands as a
   * chip above the bar that names the node and the figure, and only the chip's
   * own press (or ↵ on the emptied box) spends — the keyboard's `priced` toast
   * quotes a figure as a pass fires, but a typed name can be mistyped or
   * half-remembered, so this surface states the price BEFORE the press instead
   * (invariant 9's spirit: nothing typed ever spends silently). The two free
   * forms skip the chip — they open a writing surface and call no model, the
   * same asymmetry the card's FREE tier already draws.
   */
  const act = useMemo(() => (hasGraph ? parseAct(draft) : null), [draft, hasGraph])

  /**
   * Directives typed over an open graph join its standing brief.
   *
   * `/given`, `/constraint`, `/exclude` and `/criterion` only ever reached the
   * brief with a seed. Typed over an open graph they made a chip and nothing
   * else: ↵ on a draft holding only a directive did nothing at all, and ↵ with a
   * question beside one sent the question and dropped the directive without a
   * word — the honoured-or-dropped ambiguity the committed chips exist to close,
   * on the one command family whose whole point is to steer every LATER pass. So
   * here the press writes them through the brief's one writer, free and one step
   * of history, as a gap's "Answer it" does, and then asks whatever question
   * remains. Refused whole, and said, when the brief has no room for them.
   */
  const briefDraft = askContext && !act ? (parsed.spec.directives ?? []) : []
  const briefHold = briefDraft.length ? briefRefusal(session.brief, briefDraft) : null
  const addToBrief = (): boolean => {
    if (!briefDraft.length) return true
    if (briefHold) {
      toast(briefHold, 'warn')
      return false
    }
    const adding = briefDraft
    setBrief([...(useStore.getState().session.brief ?? []), ...adding])
    toast(briefAddedToast(adding), 'info', {
      label: 'Undo',
      run: () => {
        // Exactly the directives this press wrote, wherever they now sit —
        // never "the last step", which may since be somebody else's.
        const cur = useStore.getState()
        const now = cur.session.brief ?? []
        const mine = (d: { kind: string; text: string }) =>
          adding.some((a) => a.kind === d.kind && a.text === d.text)
        if (now.some(mine)) cur.setBrief(now.filter((d) => !mine(d)))
      },
    })
    // Out of the box, and only they: a `/moves 48` beside them still steers the
    // next run, as it did before the press.
    setDraft(withoutDirectives(draft))
    return true
  }
  /** The graph's own ↵: the brief first, then the question if one is left. */
  const submitAsk = () => {
    if (!addToBrief()) return
    if (askable) doAsk()
  }

  const [pendingAct, setPendingAct] = useState<{
    kind: Exclude<ActKind, 'record' | 'edit'>
    nodeId: string
    label: string
  } | null>(null)

  const armAct = (kind: ActKind) => {
    const s = useStore.getState()
    const sel = s.selectedId ? s.session.nodes[s.selectedId] : null
    if (!sel) {
      toast(
        'A typed verb acts on the selected node, and nothing is selected — click a node first.',
        'warn',
      )
      return
    }
    // The card's own tier rules, quoted: the seed takes the six moves, and the
    // checks, the result form and the rewrite belong to claims derived from it.
    if (sel.parentId === null && !(kind in OP_HINT)) {
      toast(
        'The seed is the question this exploration asks — checks, results and rewrites belong to the claims derived from it.',
        'warn',
      )
      return
    }
    if (kind === 'record' || kind === 'edit') {
      // Free, so no chip: the press opens a writing surface and spends nothing
      // — straight through the one dispatcher, which opens the rail first and
      // pokes a frame later (D42's frame, kept in `actPress`).
      takeAct(kind, sel.id)
      setDraft('')
      return
    }
    setPendingAct({ kind, nodeId: sel.id, label: sel.label })
    setDraft('')
  }

  /**
   * What the armed chip says before its press, over the route that press runs
   * on (`actReady`), never the selection's key: the figure, or the word standing
   * where a press will not spend — "Needs a key", or "Part of the demo" where the
   * worked example holds an authored branch for it — and its name, from the same
   * reading, as a proposal's chip is named (`actPressName`). A cross-examination
   * is read against the auditor its pass would resolve (`actAuditor`).
   */
  const armedCell = useMemo(() => {
    if (!pendingAct) return { verb: null, auditor: null, word: null, name: undefined }
    const { kind, nodeId, label } = pendingAct
    const view = { modelId, roleModels, keys, customBase, customModel }
    const verb = actPriceVerb(kind)
    const auditorEntry = actAuditor({ ...view, session }, kind, nodeId)
    const ready = actReady(view, kind, auditorEntry)
    const demo = kind in OP_HINT && hasDemoReserve(nodeId, kind as RunVerb)
    const est = verb ? projectVerbUsd({ modelId, roleModels }, verb, auditorEntry) : null
    return {
      verb,
      auditor: auditorEntry,
      word: ready ? null : demo ? STATUS_WORD.demo : STATUS_WORD.needsKey,
      name: actPressName(kind, label, pressTail({ ready, demo, est })),
    }
  }, [pendingAct, modelId, roleModels, keys, customBase, customModel, session])

  const fireAct = () => {
    const armed = pendingAct
    if (!armed) return
    // Armed against a node the graph may have moved past — pruned, or undone.
    // The chip is a promise about THAT node, so a permanent refusal stands it
    // down; a transient one (a full Up next line, or the lock on the one paid
    // act that cannot wait there) leaves it armed for the next press. Under a
    // pass in flight a move, check or panel is TAKEN — queued behind it, as
    // the same press from a key is (D162) — and the chip stands down with it.
    // The dispatcher owns every sentence.
    const gone = !useStore.getState().session.nodes[armed.nodeId]
    if (takeAct(armed.kind, armed.nodeId) || gone) setPendingAct(null)
  }

  /* The primary press: Stop while a lone pass is in flight; Ask on an open graph;
     Explore when the words start a new exploration — a blank canvas, or a
     frame command that says so outright. ⌘↵ is that same intent as a chord, and
     it is the only door that replaces an open graph without clearing it first.
     A RUN is stopped from the run row above the input, which is the one Stop a
     run has: two Stops a hand's width apart, one ending the run and one the
     same run by another name, were two answers to one question. So while a run
     is live this press goes back to asking — a question takes no lock — and the
     seed doors refuse as they always did. One button, always truthful. */
  const stopping = locked && !runLive
  const primaryDisabled = stopping
    ? false
    : act
      ? false
      : askContext
        ? !askable && !briefDraft.length
        : !canExplore
  /** A draft over an open graph that holds directives and no question: the press
   *  adds them to the brief and asks nothing. */
  const briefOnly = briefDraft.length > 0 && !askable
  const primaryTitle = stopping
    ? 'Stop generating'
    : act
      ? act.kind === 'record' || act.kind === 'edit'
        ? `${ACT_NAME[act.kind]} — opens the form on the selected node. No model call.`
        : `${ACT_NAME[act.kind]} the selected node — a chip above the bar states the price before anything is spent.`
      : askContext
        ? briefOnly
          ? `Add ${briefDraft.length === 1 ? 'this directive' : `these ${briefDraft.length} directives`} to the standing brief — every pass from here is briefed with ${briefDraft.length === 1 ? 'it' : 'them'}. No model call; undoable.`
          : askable
            ? `Ask about ${selectedId ? 'the selected node' : 'this graph'} — it answers from the graph, and asking itself changes nothing.${briefDraft.length ? ` The ${briefDraft.length === 1 ? 'directive joins' : 'directives join'} the standing brief first.` : ''} ⌘↵ starts a new exploration from these words instead.`
            : `Type a question about ${selectedId ? 'the selected node' : 'this graph'} — or ⌘↵ a claim to start a new exploration.`
        : hasGraph
          ? 'Start over from this idea  ⌘↵'
          : 'Explore this claim  ⌘↵'
  const runnable = !busy && !runLive && !keyGap && nodeTotal >= 1 && !ceilingTooLow
  // WHAT the floor bounds, in words, because it stopped being "a pass" when the
  // panel started counting its three seats. A user whose ceiling covers two of
  // those seats reads "cannot cover a single pass" as a statement about their own
  // case, and it is false there. Built once and read by both surfaces that refuse
  // a ceiling — the button's title and the panel's warning — because two
  // sentences about one figure are two facts that can disagree.
  const floorUnitOf = (d: { seats: number } | null): string =>
    d && d.seats > 1
      ? `one panel — ${d.seats} passes billed together, with no stop between them —`
      : 'a single pass'
  const floorUnit = floorUnitOf(ceilingDearest)
  /** The cap dial's figures, from the calls the tune panel's come from. */
  const capFacts: CapFacts = {
    floor: ceilingDearest,
    unit: floorUnit,
    check: checkFloor,
    moves: budget.moves,
    est: runMovesEst,
    rangeAt: (usd) =>
      projectMoveRange(
        runModels,
        budget,
        auditor?.entry ?? null,
        usd,
        scanInPlay,
        true,
        scanUsd,
        spec.execution,
      ),
  }
  const ceilingLowLine = ceilingTooLow
    ? `That ceiling cannot be guaranteed to cover ${floorUnit} on ${ceilingDearest!.entry.name}, so the run would stop before its first move.`
    : null
  const runTitle = keyGap
    ? keyGap.text
    : nodeTotal < 1
      ? 'A run works the open ends of a graph. Explore an idea first.'
      : ceilingLowLine
        ? ceilingLowLine
        : busy || runLive
          ? 'A pass is already going. Stop it, or let it finish.'
          : 'Work the open ends autonomously, under the budget above'
  /**
   * Under the opening (D351): the strip's Run holds the run until the opening
   * lands, gated as it is over any graph — a key the run needs, a ceiling under
   * one pass — and once, since a held run waits with Cancel in its place.
   */
  const underOpening = opening && !runLive
  const holdable = underOpening && !held && !keyGap && !ceilingTooLow
  const holdTitle = keyGap
    ? keyGap.text
    : ceilingLowLine
      ? ceilingLowLine
      : `When the opening lands, work its open ends on their own: up to ${budget.moves} moves${ceilingSet ? `, stopping before the next pass could cross your ${fmtUsd(ceilingUsd as number)} cap` : ''}${runEst !== null ? ` — about ${fmtUsd(runEst)} at list prices, an estimate that leans high, not a limit` : ''}. A Stop, or an opening that fails, starts nothing.`

  /**
   * The rest of the last run, offered as the run press, where its ending left one.
   *
   * After a Stop, "Let it run" used to start a brand-new run at move 1 of the
   * dials' budget and never mention the moves the stopped one had left. After a
   * Stop, and after an API that stopped answering (D337) — a rate limit, an
   * outage, an empty account, none of them a judgment about the graph. Every
   * other ending is the run's own stop condition, and a press that continued past
   * one would overrule it (`continuationOf`). The remainder is the record's; the
   * steering is the dials', because the record does not carry it. It is still a
   * press — nothing here starts a run, and nothing retries one — and the figure
   * is the same projection the plain press shows, over the remainder's budget.
   */
  const lastRun = session.runs.at(-1)
  const continuation = useMemo(() => {
    const rest = lastRun ? continuationOf(lastRun) : null
    if (!rest) return null
    const contSpec: RunSpec = {
      ...rest,
      execution: spec.execution,
      ...(spec.detail ? { detail: spec.detail } : {}),
      ...(spec.mode !== undefined ? { mode: spec.mode } : {}),
      ...(spec.ladder?.length ? { ladder: spec.ladder } : {}),
      ...(spec.depth !== undefined ? { depth: spec.depth } : {}),
    }
    const contBudget = budgetFor(contSpec, rest.usdMax ?? null)
    const contScan = scanIsReachable(contBudget)
    // The pass reserve, as for a fresh run: a continuation is an ordinary run, and
    // what is left of an ended run's cap can be under a check's and still reason.
    const floor =
      ceilingFloors(
        runModels,
        (contBudget.audits ?? 0) >= 1 ? (auditor?.entry ?? null) : null,
        rolesInPlay(contBudget, contScan),
      )?.pass ?? null
    return {
      spec: contSpec,
      // Its own dials' keys: the rest of an ended run schedules what it had left.
      keyGap: runKeyGap(
        { modelId, roleModels, keys, customBase, customModel },
        rolesInPlay(contBudget, contScan),
        contSpec.audits ?? 0,
      ),
      moves: rest.moves,
      est: projectRunPressUsd(
        { modelId, roleModels },
        runModels,
        contBudget,
        auditor?.entry ?? null,
        contScan,
        scanUsd,
        contSpec.execution,
      ),
      // What is left of the ended run's ceiling can be too little for one more
      // pass, and the press says so rather than refusing after it.
      tooLow:
        rest.usdMax !== null && rest.usdMax !== undefined && !!floor && rest.usdMax < floor.usd,
      left: rest.usdMax ?? null,
      floor,
    }
  }, [
    lastRun,
    spec,
    runModels,
    auditor,
    scanUsd,
    modelId,
    roleModels,
    keys,
    customBase,
    customModel,
  ])
  const contRunnable =
    continuation !== null &&
    !busy &&
    !runLive &&
    !continuation.keyGap &&
    nodeTotal >= 1 &&
    !continuation.tooLow
  const contTitle = !continuation
    ? ''
    : continuation.keyGap
      ? continuation.keyGap.text
      : continuation.tooLow && continuation.left !== null && continuation.floor
        ? `What is left of the last run’s ceiling, ${fmtUsd(continuation.left)}, cannot be guaranteed to cover ${floorUnitOf(continuation.floor)} on ${continuation.floor.entry.name}. Start over under a new cap instead.`
        : contRunnable
          ? `Pick up where the run left off: its ${continuation.moves} unspent move${continuation.moves === 1 ? '' : 's'}${continuation.left !== null ? `, under what is left of its ceiling (${fmtUsd(continuation.left)})` : ''}${continuation.est !== null ? ` — about ${fmtUsd(continuation.est)} at list prices, an estimate that leans high, not a limit` : ''}`
          : runTitle
  const continueRun = () => {
    if (!continuation || !contRunnable) return
    void startRun(continuation.spec)
  }

  /**
   * The run strip at rest, from the figures above and nowhere else: the tune
   * panel and this strip quote one projection, so neither can describe the
   * next run differently. The count and the cap are the
   * DRAFT's as well as the dials' — a typed `/moves 24` is what the press will
   * run, so it is what the strip says before the press.
   */
  const adjustOpen = useAdjustOpen()
  const runRest: RunRest = {
    // Under the opening too: the run will work the graph it is landing.
    show: nodeTotal >= 1 || underOpening,
    runnable: underOpening ? holdable : runnable,
    title: underOpening
      ? holdTitle
      : runnable
        ? `Work the open ends on their own: up to ${budget.moves} moves${ceilingSet ? `, stopping before the next pass could cross your ${fmtUsd(ceilingUsd as number)} cap` : ''}${runEst !== null ? ` — about ${fmtUsd(runEst)} at list prices, an estimate that leans high, not a limit` : ''}`
        : runTitle,
    afterOpening: underOpening,
    held: underOpening && held ? { moves: held.moves, est: held.est, onDrop: dropHeldRun } : null,
    est: runEst,
    moves: budget.moves,
    cap: ceilingSet ? (ceilingUsd as number) : null,
    needsKey: keyGap !== null,
    // The floor, said on the strip in words (D154): a cap the run cannot start
    // under greys the press, and a greyed press with its reason in a tooltip is
    // the money moment the study found worst. A key the run needs is said the
    // same way once the reader holds some key — "Needs a key" beside a key they
    // stored reads as a fault — in the press's own refusal, which names the
    // route and, where a dial scheduled it, that it can be turned off.
    why:
      keyGap && keyHeld
        ? keyGap.text
        : ceilingTooLow && ceilingUsd !== null
          ? `${capRangeLine(capFacts, ceilingUsd) ?? runTitle} Raise it, or drop it, under Adjust.`
          : null,
    onRun: letItRun,
    continuation: continuation && {
      moves: continuation.moves,
      est: continuation.est,
      left: continuation.left,
      runnable: contRunnable,
      title: contTitle,
      // A refusal first. Otherwise, after a run the API stopped answering, the
      // last failure's own sentence — once the ending's head that said it has
      // been closed or a reload has passed, so Continue is never the one primary
      // with no word that the provider was failing and may fail again (D337).
      why:
        (continuation.keyGap && keyHeld) || continuation.tooLow
          ? contTitle
          : endingShown
            ? null
            : unreachableWhy(lastRun),
      onRun: continueRun,
    },
    dials: `${LENS_LABEL[lens]} · ${TEMPER_LABEL[temper]} · ${budget.moves} moves · ${ceilingSet ? `${fmtUsd(ceilingUsd as number)} cap` : 'no cap'}`,
  }
  /** Lens and temper on Adjust's face, over a graph: the strip's own words name
   *  the count and the cap, and these two change every pass, not only a run's.
   *  On a blank canvas the strip's lead already says all four. */
  const adjustFace = hasGraph ? `${LENS_LABEL[lens]} · ${TEMPER_LABEL[temper]}` : ''

  /* Everything a committed box is still steering, kept as chips AFTER the box
     clears. These used to survive only for ladder, depth and until — the three
     with no dial in the tune panel — so seven typed commands left one pill
     standing after the press, and there was no way to tell honoured from
     dropped. The whole row persists now; see steerChips.ts for the contract. */
  const committed = useMemo(
    () =>
      committedChips({
        spec: parsed.spec,
        tune: runTune,
        ceiling: runCeiling,
        defaults: tuneDefaults,
        frame: session.frame,
        brief: session.brief,
      }),
    [parsed.spec, runTune, runCeiling, tuneDefaults, session.frame, session.brief],
  )
  const clearCommitted = (c: CommittedClear) => {
    if (c.kind === 'tune') setRunTune({ ...runTune, ...resetPatch(c.field, tuneDefaults) })
    else if (c.kind === 'ceiling') setRunCeiling('')
    else setBrief((session.brief ?? []).filter((_, j) => j !== c.at))
  }
  /** The bar's slice of the committed row: the run strip already states the
   *  move count and the cap above the input (they were the rail's MOVES and
   *  BUDGET chips), and a pill restating them is the mode chip D19 retired. */
  const barChips = committed.filter((c) => c.restates !== 'rail')
  /** The panel's Steering section: only what its own dials do not already say. */
  const steerChips = committed.filter((c) => c.restates === null)

  const keyInDraft = useMemo(() => looksLikeProviderKey(draft), [draft])
  const warnings: string[] = [
    // A draft that IS a typed act reaches `parseCommands` as one unknown
    // command, whose "left in the idea" note would warn about the very thing
    // the act branch is about to do.
    ...(act ? [] : parsed.problems),
    // The brief's cap and a directive's length, before the press rather than
    // after a reload: over a graph the directives join its brief, and with a
    // seed they are the new exploration's.
    ...(briefHold ? [briefHold] : []),
    ...(seedBriefHold && !askContext ? [seedBriefHold] : []),
    ...(parsed.spec.focus && !selectedId
      ? [
          '/focus works on the selected node, and nothing is selected — click a node first, or the run will work the whole graph.',
        ]
      : []),
    ...((parsed.spec.audits ?? 0) > 0 && !auditor
      ? [
          'A cross-examination needs a key for a second provider, so this run would refuse to start. Add one, or drop the /audit.',
        ]
      : []),
    ...(parsed.spec.replicate && !auditor
      ? [
          '/replicate needs a key for a second provider — both halves on one house would be a comparison that compares nothing.',
        ]
      : []),
    // A key pasted into the box, said before it goes anywhere (D335). The splash
    // asks for a key and this is the most obvious field on the page; pressed, the
    // words are a seed or a question — sent to whichever house is routed, kept
    // with the exploration, and carried by its exports and share links. Known
    // prefixes only (`looksLikeProviderKey`), so prose never trips it, and a
    // note rather than a refusal: the press stays the reader's.
    ...(keyInDraft
      ? [
          `That looks like ${/^[AEIOU]/.test(PROVIDERS[keyInDraft].keyLabel) ? 'an' : 'a'} ${PROVIDERS[keyInDraft].keyLabel}. Sent from here it would go to the model as text and stay with the exploration — keys belong in Settings → Providers.`,
        ]
      : []),
  ]

  /**
   * The ask row's pieces — the sources +, the field and the press — named once
   * and laid out by where the composer stands (D353). Over the field they are
   * one row, the sheet's. Docked in the thread's column they are the owner's
   * design: the field on a line of its own, and under it the +, the
   * exploration's account and the press.
   */
  const sourcesPress = (
    <>
      {/* Row 2 opens with the sheet's + — what this run may draw on. Green
        once files are attached. It opens a popover listing them, per the
        sheet, and the full dialog is one click deeper. */}
      <div style={{ position: 'relative', flex: 'none' }}>
        <button
          type="button"
          className="csrc"
          data-on={corpusCount > 0 || undefined}
          aria-expanded={srcOpen}
          aria-haspopup="dialog"
          // The name and the sentence are two different lengths of the same
          // thing. A `title` alone left this button announcing as "plus":
          // the icon carries no word, and the count belongs in the sentence
          // rather than in the name, which has to stay the same button.
          aria-label="What this run may draw on"
          title={
            corpusCount > 0
              ? `${corpusCount} private source${corpusCount === 1 ? '' : 's'} attached — cited by filename, and they outrank anything the model recalls on its own`
              : 'What this run may draw on — attach private sources that outrank anything the model recalls on its own'
          }
          onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect()
            setSrcAnchor({ left: r.left, up: window.innerHeight - r.top })
            setSrcOpen((o) => !o)
          }}
        >
          <Icon name="plus" size={15} />
        </button>
        <SourcesPopover
          open={srcOpen}
          anchor={srcAnchor}
          onClose={() => setSrcOpen(false)}
          onOpenDialog={() => {
            setSrcOpen(false)
            setSources(true)
          }}
        />
      </div>
    </>
  )
  const askField = (
    <input
      ref={inputRef}
      value={draft}
      onChange={(e) => {
        setDraft(e.target.value)
        captureCaret(e.target)
      }}
      onSelect={(e) => captureCaret(e.currentTarget)}
      onFocus={(e) => {
        setFocused(true)
        captureCaret(e.currentTarget)
      }}
      onBlur={() => {
        setFocused(false)
        savedCaret = null
      }}
      onKeyDown={(e) => {
        // Bare arrows only. ⌥↓, ⇧↑ and ⌘↓ are the platform's own
        // text-editing gestures inside a field, and a completion list is
        // not worth taking them away from someone editing a claim.
        const bare = !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey
        if (completions.length > 0 && bare && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
          e.preventDefault()
          walkCompletions(e.key === 'ArrowDown' ? 1 : -1)
          return
        }
        // Escape is deliberately unbound for the completion list — it
        // lives and dies with the /token under the caret, so swallowing
        // Escape would cost the global ladder its first rung for the
        // price of nothing. An ARMED ACT is different: it is a layer that
        // outlives the keystroke that raised it, standing over the bar
        // with a priced press on it, and Escape putting it down is the
        // platform's own grammar. Stopped from propagating so the ladder
        // does not also deselect the very node the chip names.
        if (e.key === 'Escape' && pendingAct) {
          e.preventDefault()
          e.stopPropagation()
          setPendingAct(null)
          return
        }
        if (e.key !== 'Enter') return
        e.preventDefault()
        // A walked-to completion takes ↵ before the box does, and only
        // when one has been walked to. ⌘↵ still means what it says
        // below — always the exploration — so it never lands here.
        if (bare && cmpAt >= 0) {
          complete(completions[cmpAt]!.name)
          return
        }
        // ⌘↵ is always the exploration — the explicit "these words are a
        // new claim" gesture. Plain Enter follows the words' meaning here:
        // a typed act arms its chip, a second ↵ on the emptied box fires
        // it, and otherwise the words are a question about the open
        // graph, or a claim on an empty one. ⇧↵ is plain ↵: a run after
        // the opening is the strip's Run, pressed while it goes (D351).
        if (e.metaKey || e.ctrlKey) explore()
        else if (act) armAct(act.kind)
        else if (pendingAct && !draft.trim()) fireAct()
        else if (askContext) submitAsk()
        else explore()
      }}
      role="combobox"
      aria-autocomplete="list"
      aria-expanded={completions.length > 0}
      aria-controls={completions.length > 0 ? cmpListId : undefined}
      aria-activedescendant={cmpActiveId}
      // The same three branches as the placeholder, deliberately. This had
      // no `selectedId` branch, so a screen-reader user with a node
      // selected heard "graph" while sighted users read "node" — two
      // names for the same press, one of them wrong.
      aria-label={
        !hasGraph
          ? 'What are you trying to figure out'
          : selectedId
            ? 'Ask about this finding'
            : 'Ask about this graph'
      }
      // No trailing ellipsis, unlike the placeholders elsewhere in the
      // tree: these are short imperatives, and an ellipsis on one reads as
      // "still loading" rather than "keep typing".
      placeholder={
        !hasGraph
          ? 'What are you trying to figure out? Type /commands to customize'
          : selectedId
            ? 'Ask about this finding'
            : 'Ask about this graph'
      }
      style={{
        // A floor, not zero: on a tight stage the chip and button yield
        // before the box does — a composer with no visible input is not a
        // composer.
        minWidth: 110,
        // Docked, the field has a line of its own (D353), at the design's
        // two-row height with its words on the first row (D356): it stays one
        // line of input — Enter still sends — and only looks like the box.
        ...(docked
          ? { flex: 'none', height: 46, padding: '2px 0 23px', lineHeight: '21px' }
          : { flex: 1, height: '100%' }),
        background: 'transparent',
        border: 0,
        outline: 'none',
        fontFamily: 'var(--font-display)',
        fontSize: '14px',
        letterSpacing: 0,
        color: 'var(--text-1)',
      }}
    />
  )
  const pressWord = stopping
    ? 'Stop'
    : act
      ? ACT_NAME[act.kind]
      : askContext
        ? briefOnly
          ? 'Add'
          : 'Ask'
        : // One word for both seed states, and the app's own: the box asks what
          // you are trying to figure out, the hover and the screen reader say
          // explore, and what lands is called an exploration everywhere else.
          // "Seed" and "State" were the kind and the frame — internal names on
          // the first press a new reader makes (D335).
          'Explore'
  // Docked, the press is the design's square arrow while it does what the box
  // does by default — ask about the claim (D356). Any other press still says
  // its word, Stop above all: a word is the only thing that tells it apart.
  const squareSend = docked && !stopping && !act && askContext && !briefOnly
  const primaryPress = (
    <>
      {/* No mode chip here. The sheet's input row is exactly three things —
      the sources +, the field, and the primary button — and a chip
      between the last two spent the row restating LENS and TEMPER, which
      are already the first two dials under Adjust on the run row above.
      That row IS the tune surface; the tune panel is the same dials in a
      taller form, on ⌘K → "Customize the next run". And it is where a
      run after Explore starts: Run, pressed while the opening goes
      (D351). The split press that chained them sat here, a second door
      to the run beside the one press that seeds. */}
      <button
        {...runBind}
        onClick={
          stopping ? cancelCurrent : act ? () => armAct(act.kind) : askContext ? submitAsk : explore
        }
        disabled={primaryDisabled}
        title={primaryTitle}
        aria-label={
          stopping
            ? 'Stop the pass in flight'
            : act
              ? `${ACT_NAME[act.kind]} the selected node`
              : askContext
                ? briefOnly
                  ? 'Add to the standing brief'
                  : selectedId
                    ? 'Ask about the selected finding'
                    : 'Ask about this graph'
                : hasGraph
                  ? 'Start a new exploration from these words'
                  : 'Start exploring this idea'
        }
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          flex: 'none',
          // 30 on the desktop, 44 on a phone (`--cprimary-h`, set in the
          // phone block of global.css): this press is Stop while anything
          // is in flight, and Stop is a thumb's target there.
          height: 'var(--cprimary-h, 30px)',
          width: squareSend ? 'var(--cprimary-h, 30px)' : undefined,
          justifyContent: 'center',
          padding: squareSend ? 0 : '0 12px',
          // The thread's press (D352): its word in the thread's own type,
          // where it was nine-and-a-half-pixel mono capitals on a pill.
          borderRadius: 8,
          fontFamily: 'var(--font-sans)',
          fontSize: '13px',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          // Tokens, not literals, and the reason is the Stop state: its ink
          // was a single dark-tuned value with no light fork, so on the
          // plate it composited to about 1.3:1 — unreadable on the ONE
          // control that halts a paid run, which Escape deliberately will
          // not do. `--ink-tension` is the theme-aware tier for exactly
          // this. The resting fill is the medium's own ink with its
          // reserved counter-colour on top; the old pair were blue-cast
          // near-white and near-black, which the token file's header
          // forbids in so many words.
          // Docked and with nothing to send, the design's quiet well and its
          // third ink, rather than the ink faded (D356).
          color: stopping
            ? 'var(--ink-tension)'
            : docked && primaryDisabled
              ? 'var(--text-3)'
              : 'var(--text-inverse)',
          background: stopping
            ? 'color-mix(in srgb, var(--sem-tension) 16%, transparent)'
            : docked && primaryDisabled
              ? 'color-mix(in srgb, var(--text-1) 8%, transparent)'
              : 'var(--text-1)',
          // Flat, the thread's primary: the hover lifts the ink a step
          // rather than lighting a glow round it.
          filter: runHov && !primaryDisabled && !stopping ? 'brightness(1.15)' : undefined,
          opacity: primaryDisabled && !docked ? 0.38 : 1,
          cursor: primaryDisabled ? 'not-allowed' : 'pointer',
          transition: 'background 130ms ease, filter 130ms ease',
        }}
      >
        {!squareSend && pressWord}
        {/* The send arrow, the stop square: drawn, so the name stays the word. */}
        <Icon name={stopping ? 'stop' : 'arrowUp'} size={13} />
      </button>
    </>
  )

  /** The bar's rows under the run: the node's line, the modes, and the ask row. */
  const barRows = (
    <>
      {/* Row 1 — the selected node's line, the sheet's dot-and-title. */}
      {selNode && (
        <div className="cnode">
          <i style={{ background: `var(--sem-${channelOf(selNode)})` }} />
          <span>{selNode.label}</span>
        </div>
      )}
      {/* The modes, under the line that names what they act on (D347): Ask is
        the box below, and the other three put their presses in its place.
        The box stays mounted while they show — its draft, its caret and its
        refs are the composer's, whichever mode is up. */}
      {modeNode && mode && <ModeTabs node={modeNode} mode={mode} id={modeId} />}
      {modeNode && mode && mode !== 'ask' && <ModePanel node={modeNode} mode={mode} id={modeId} />}
      <div
        id={modeNode && mode === 'ask' ? `${modeId}-panel` : undefined}
        role={modeNode && mode === 'ask' ? 'tabpanel' : undefined}
        aria-labelledby={modeNode && mode === 'ask' ? `${modeId}-ask` : undefined}
        className={docked ? 'cask cask--docked' : 'cask'}
        style={{ display: mode && mode !== 'ask' ? 'none' : 'flex' }}
      >
        {docked ? (
          <>
            {askField}
            <div className="cask-row">
              {sourcesPress}
              {/* The exploration's account, where the owner's design puts it:
                  the foot's line, from the foot's own hook (D353). */}
              {hasGraph && (
                <span className="cask-meta" title={SPEND_TITLE}>
                  {account}
                </span>
              )}
              {primaryPress}
            </div>
          </>
        ) : (
          <>
            {sourcesPress}
            {askField}
            {primaryPress}
          </>
        )}
      </div>
    </>
  )

  // Docked, the rows above the box are cards of the column, as the box is
  // (D353); over the field they are the one glass.
  const aboveGlass: CSSProperties = {
    ...(docked
      ? {
          background: 'var(--surface-solid)',
          border: '1px solid color-mix(in srgb, var(--text-1) 7%, transparent)',
          borderRadius: 12,
        }
      : { ...glass(), borderRadius: 'var(--r-glass)' }),
    padding: '8px 12px',
    // Opted in by hand, like every control above the bar: the wrapper and the
    // column pass presses, so the gaps between rows reach what is behind them.
    pointerEvents: 'auto',
  }

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        // So the column above the bar can yield height in the pane — see
        // `.cabove`. The bar itself is `flex: none` below and never does.
        minHeight: 0,
        // Presses pass through this wrapper and the column inside it; only the
        // rows, the bar and the popovers take them, each opted in by hand. The
        // rows are in the flow now, so over the field this box covers the
        // transparent gaps beside a centred chip row and between rows, and a
        // wrapper that took presses there would make them a dead zone over the
        // canvas — the absolute column this replaced carried the same `none`
        // for the same reason. Nothing here relies on inheritance going the
        // other way: `.slot-composer` passes too, so a control that forgets to
        // opt in is unreachable and shows up the first time it is pressed.
        pointerEvents: 'none',
      }}
    >
      {/* The rows above the bar: the box's own completions, chips, notes and
          warnings, and the armed act. IN THE FLOW, not floated over whatever
          stands behind the bar. This was an absolutely positioned column from
          the days when the only thing behind the bar was the field, and it
          outlived that premise: once the composer moved into the thread pane
          the same column stood over the feed's last lines and the pane's foot —
          a seven-command draft hid Prune under its own pills, and every pill
          took the presses meant for the text beneath it. In the flow, the feed
          and the foot make room instead; over the field the strip is anchored
          by its bottom edge, so the bar stays put and the rows still stack
          upward from it, pushing the thought stream up rather than covering
          it. `.cabove` in global.css holds the rest of the contract: a scroll
          container, so a squeezed pane scrolls the rows rather than pushing the
          bar off its bottom edge, and `:empty` collapses the column so an empty
          one seats no gap. Hidden rather than unmounted under the tune panel:
          the panel floats over exactly this space, and a column that unmounted
          would drop the bar by its own height on every open and close. */}
      <div className="cabove scroll" style={{ visibility: tuneOpen ? 'hidden' : undefined }}>
        {completions.length > 0 && (
          <div
            id={cmpListId}
            role="listbox"
            aria-label="Commands"
            style={{ ...aboveGlass, width: '100%', display: 'grid', gap: 2 }}
          >
            {completions.map((h) => (
              <CompletionRow
                key={h.name}
                id={cmpRowId(h.name)}
                name={h.name}
                hint={h.hint}
                theme={theme}
                active={h.name === cmpName}
                onPick={complete}
              />
            ))}
          </div>
        )}
        {(autoQuestion || parsed.chips.length > 0 || barChips.length > 0) && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              alignItems: 'stretch',
              gap: 4,
              pointerEvents: 'auto',
            }}
          >
            {autoQuestion && (
              <Chip
                label="read as a question"
                title="An open question: the first branches will be the field's competing answers. ✕ reads it as a claim instead."
                onClear={() => setQuestionDeclined(true)}
              />
            )}
            {parsed.chips.map((c) => (
              <Chip
                key={c.name}
                label={c.label}
                title={c.source}
                onClear={() => dropChip(c.source)}
              />
            ))}
            {/* One row, two lifetimes: left of the rule is the draft's
                    promise (its ✕ un-types), right of it is already in force
                    (its ✕ writes the store). Identical pills for both left a
                    reader unable to tell "will apply" from "applies". */}
            {(autoQuestion || parsed.chips.length > 0) && barChips.length > 0 && (
              <i aria-hidden style={{ width: 1, background: 'var(--hairline-strong)' }} />
            )}
            {barChips.map((c) => {
              const cl = c.clear
              return (
                <Chip
                  key={c.key}
                  label={c.label}
                  title={c.title}
                  onClear={cl ? () => clearCommitted(cl) : undefined}
                  onOpen={
                    c.open === 'brief'
                      ? () => {
                          briefAsked = true
                          useStore.getState().setTune(true)
                        }
                      : undefined
                  }
                />
              )
            })}
          </div>
        )}
        {suggestion && !askContext && (
          <div style={{ ...aboveGlass, ...NOTE, color: 'var(--text-2)' }}>
            That reads like a {suggestion}.{' '}
            <button
              type="button"
              className="lnk"
              onClick={() => setDraft(`/${suggestion} ${draft.trimStart()}`)}
            >
              Explore it as one
            </button>{' '}
            — branches become{' '}
            {suggestion === 'question' ? 'competing answers' : 'routes and blockers'} instead of
            supports and objections.
          </div>
        )}
        {/* The dials' reading, under the frame's because the frame is the
                bigger reframe. Seed context only, like the note above it: over
                an open graph the words are a question, and a lens or temper
                change there would also rewrite the live exploration's prompt
                prefix mid-stream — the exact cost D50 measures. And never while
                a run is live: a frame command makes these words a seed, but the
                seed waits on the run, and the dials do not — a press sold as
                tuning the draft would land its writes on the passes in flight.
                The dials themselves stay reachable in the tune panel (⌘K)
                meanwhile, as ever; it is the OFFER that holds until its writes
                can only mean what it says. Each clause prints the why so nothing
                here is a verdict handed down. */}
        {tuneCues.length > 0 && !askContext && !runLive && (
          <div style={{ ...aboveGlass, ...NOTE, color: 'var(--text-2)' }}>
            These words suggest{' '}
            {tuneCues
              .map((c) =>
                c.dial === 'lens'
                  ? `the ${LENS_LABEL[c.lens]} lens (${c.why})`
                  : c.dial === 'temper'
                    ? `${TEMPER_LABEL[c.temper]} (${c.why})`
                    : `${c.moves} moves (${c.why})`,
              )
              .join('; ')}
            .{' '}
            <button type="button" className="lnk" onClick={applyTune}>
              Set the dials so
            </button>
          </div>
        )}
        {/* SHARPEN — one small pass on the person's own words, before any
            graph exists, and the only pass in this app that runs on a draft.
            It lands nothing: each candidate is a chip, and pressing one
            fills the box, which the person can then edit or explore. Offered
            only where the words would BE a seed — over an open graph they
            are a question, and there is nothing to sharpen. */}
        {!askContext && !runLive && canExplore && (
          <div style={{ ...aboveGlass, ...NOTE, color: 'var(--text-2)' }}>
            {sharp.length === 0 ? (
              <>
                Before you spend on this,{' '}
                <button
                  type="button"
                  className="lnk"
                  disabled={sharpening || busy || !canCallModel}
                  onClick={() => {
                    setSharpening(true)
                    // Rewordings of one reader's words are theirs: a pass that
                    // lands after the reader changed is dropped, not offered
                    // under the next one's box.
                    const reader = conversationOwnerTag()
                    void sharpenSeed(parsed.seed, nextFrame)
                      .then((found) => {
                        if (conversationOwnerTag() === reader) setSharp(found)
                      })
                      .finally(() => setSharpening(false))
                  }}
                  title={
                    canCallModel
                      ? 'One small pass turns your words into versions that could be wrong. It writes nothing — you pick, or keep your own.'
                      : 'Sharpening is a model pass, so it needs your key.'
                  }
                >
                  {sharpening ? 'sharpening…' : 'sharpen what you wrote'}
                </button>
                . One small pass; it fills the box, nothing else.
              </>
            ) : (
              <>
                Sharper versions of your idea — each could be wrong, which is what makes it worth
                exploring. Pressing one only fills the box.
                <span style={{ display: 'grid', gap: 4, marginTop: 6 }}>
                  {sharp.map((c, i) => (
                    <button
                      key={i}
                      type="button"
                      className="lnk"
                      style={{ textAlign: 'left' }}
                      onClick={() => {
                        setDraft(c.claim)
                        setSharp([])
                      }}
                      title={`Read as a ${c.frame}. ${c.why}`}
                    >
                      {c.claim}
                      <span style={{ color: 'var(--text-3)' }}> — {c.why}</span>
                    </button>
                  ))}
                </span>
              </>
            )}
          </div>
        )}
        {warnings.map((w) => (
          <div key={w} style={{ ...aboveGlass, ...NOTE, color: 'var(--ink-refuted)' }}>
            {w}
          </div>
        ))}
        {/* The next step for the selected claim (D349): the one press its record
            asks for, with the record that asked, above the bar where every
            press against a node lives (D347). Not while an act is armed: that
            one is already the press the bar's ↵ fires. */}
        {modeNode && !pendingAct && (
          <NextStepCard nodeId={modeNode.id} style={{ ...aboveGlass, width: '100%' }} />
        )}
        {/* The armed act: the same statement a card's button makes — the
                verb, the node, the figure — standing still until a person
                presses it. LAST in this column, so it sits nearest the bar
                whose ↵ fires it. */}
        {pendingAct && (
          <div
            className="actchip"
            // The column centres its children at content width, and this
            // row holds a node's whole label — unconstrained it ran past
            // the pane. The completion list pins itself the same way.
            style={{ ...aboveGlass, width: '100%' }}
          >
            <span className="actchip-kind" style={{ color: THOUGHT_TINT[pendingAct.kind] }}>
              {ACT_NAME[pendingAct.kind]}
            </span>
            <span className="actchip-target" title={pendingAct.label}>
              {pendingAct.label}
            </span>
            {/* Always non-null here — the free kinds never arm a chip — but
                    narrowed by render rather than asserted. */}
            {armedCell.verb && (
              <VerbPrice
                verb={armedCell.verb}
                auditor={pendingAct.kind === 'audit' ? armedCell.auditor : undefined}
                instead={armedCell.word ?? undefined}
              />
            )}
            {/* Named as a proposal's chip is (D165): the act, the claim, and
                what the cell says — "Run ↵" was all a screen reader heard. The
                ↵ is the box's key, not a word of the name. */}
            <button className="btn btn--primary" onClick={fireAct} aria-label={armedCell.name}>
              Run
              <span className="kbd" aria-hidden style={{ marginLeft: 4 }}>
                ↵
              </span>
            </button>
            <button
              className="actchip-dismiss"
              aria-label="Put the act down"
              title="Put it down  Esc"
              onClick={() => setPendingAct(null)}
            >
              <Icon name="close" size={11} />
            </button>
          </div>
        )}
      </div>

      {/* The tune panel's seat, while it is open: a zero-height anchor in the
          flow at the BAR's top edge, so the panel floats over the rows' space
          from the same seat whether the column above is empty or ten pills
          tall, and its 62vh cap keeps measuring against the window rather than
          against the room the rows left it. It is the one thing above the bar
          that stays out of the flow, on purpose: its content runs to some
          1200px of dials and already scrolls inside that cap, and a panel made
          to fit above the feed's floor would be a letterbox in an 800px
          window. A transient dialog may cover the feed; a standing row may
          not. */}
      {tuneOpen && (
        <div style={{ position: 'relative', flex: 'none', height: 0 }}>
          <div
            style={{
              position: 'absolute',
              bottom: 10,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <TunePanel
              theme={theme}
              budget={budget}
              auditorName={auditor?.entry.name ?? null}
              auditorProvider={auditor ? PROVIDERS[auditor.entry.provider].name : null}
              ceilingFloor={ceilingDearest}
              checkFloor={checkFloor}
              floorUnit={floorUnit}
              ceilingTooLow={ceilingTooLow}
              ceilingSet={ceilingSet}
              ceilingUsd={ceilingUsd}
              runEst={runEst}
              runMovesEst={runMovesEst}
              voice={voice}
              runModels={runModels}
              spec={spec}
              live={live}
              moveRange={moveRange}
              steerChips={steerChips}
              onClearSteer={clearCommitted}
              rev={rev}
              onRun={letItRun}
              runnable={runnable}
              runTitle={runTitle}
            />
          </div>
        </div>
      )}

      {/* The bar itself. The focus ring is an outline rather than a box-shadow
          append: the glass shell owns its shadow stack, and an outline follows
          the radius without clipping. `flex: none` because the column above may
          yield height and the bar never does; `pointerEvents` because the
          wrapper passes presses and the bar is the thing that takes them. The
          thread's card radius and a neutral ring (D352): the accent is the
          canvas's and the selection's, and a typing caret is neither. */}
      {/* Docked in the thread's column, the run is a card of its own above the
          box (D353), as the owner's design lays the column's foot: the strip's
          rows are the run's, and the box under them is the question's. Over the
          field it stays the bar's first row, under the one glass. */}
      {docked && (
        <div className="cstrip">
          <RunStrip
            rest={runRest}
            adjust={{ open: adjustOpen, panel: <AdjustPanel cap={capFacts} />, face: adjustFace }}
          />
        </div>
      )}
      {docked ? (
        // A surface of the column, not the floating material over the field:
        // the column is solid (D353), and its box is a step above its ground.
        <div className="cbox" data-focused={focused || undefined}>
          {barRows}
        </div>
      ) : (
        <GlassShell
          style={{
            flex: 'none',
            pointerEvents: 'auto',
            outline: focused
              ? '1.5px solid color-mix(in srgb, var(--text-1) 22%, transparent)'
              : 'none',
            transition: 'outline-color 130ms ease',
          }}
          contentStyle={{
            flexDirection: 'column',
            alignItems: 'stretch',
            padding: '0 9px 0 18px',
          }}
        >
          {/* Row 0 — the run: at rest its press and Adjust, live its move and the
              one Stop, ended its head (ui/RunStrip.tsx). Above the node's line
              rather than below the input, where the chip rail stood: the line and
              the input are one statement ("ask about THIS node"), and the run is
              a different one that should not come between them. */}
          <RunStrip
            rest={runRest}
            adjust={{ open: adjustOpen, panel: <AdjustPanel cap={capFacts} />, face: adjustFace }}
          />
          {barRows}
        </GlassShell>
      )}
    </div>
  )
}

/**
 * Anything that must float free of the composer's glass shell.
 *
 * The sources popover below — and the chip rail's four, while there was a chip
 * rail — opens UPWARD out of a bar 154px tall, so they only ever worked by
 * escaping it. They used to do that with `position: fixed` alone, and
 * the comments here said so: fixed was "the one escape that needs no portal,
 * because the shell makes no containing block". That premise died when the shell
 * got its real frost — `backdrop-filter` makes an element a containing block for
 * fixed descendants, so the popovers started resolving `bottom`/`left` against a
 * 154px box instead of the viewport AND inheriting its `overflow: clip`. The lens
 * menu is 387px tall; 36 of those pixels landed inside the clip, behind the
 * composer's own content. Nothing threw, `aria-expanded` went true, and the menu
 * was simply not on screen.
 *
 * `document.body` and not the stage: `main.stage` carries `contain: strict`,
 * which is also a containing block and also clips. It happens to be viewport-sized
 * today, so it hides the bug rather than causing it — portalling past it too means
 * one less thing that has to stay full-bleed for these menus to be visible.
 *
 * The frost has since gone (D358), and with it the shell's containing block, but
 * the portal stays, and was always the right fix at this level: it never
 * depended on the shell being plain, the stage around the shell still is a
 * containing block, and re-anchoring the popovers to the shell would put a 387px
 * menu inside a 154px clip — the same invisibility, arrived at deliberately.
 */
const Floating = ({ children }: { children: ReactNode }) => createPortal(children, document.body)

/** The file badge, from a trailing extension on the title if it carries one.
 *  Typed titles usually carry none, so the honest fallback is a generic 'DOC'
 *  rather than a guessed format. A document read from a file does carry one:
 *  `sourceFile.ts` keeps the filename verbatim as the title precisely so that a
 *  chip reading PDF is a fact about the document rather than a guess. */
const docExt = (title: string): string => {
  const m = /\.([a-z0-9]{1,5})$/i.exec(title.trim())
  return (m ? m[1] : 'DOC').toUpperCase().slice(0, 4)
}

/**
 * The sheet's "what this run may draw on" popover, opened by the composer's +.
 *
 * It lists the private corpus, lets a source be dropped, and hands off to the
 * full dialog to attach more — the same `localStorage` the source check reads,
 * so what shows here is exactly what a run would cite. Fixed AND portalled, for
 * the same reason the chip rail's popovers were: see `Floating`.
 */
function SourcesPopover({
  open,
  anchor,
  onClose,
  onOpenDialog,
}: {
  open: boolean
  anchor: { left: number; up: number } | null
  onClose: () => void
  onOpenDialog: () => void
}) {
  const rev = useStore((s) => s.rev)
  const [docs, setDocs] = useState<CorpusDoc[]>([])
  useEscapeToClose(open, onClose)
  // Re-read on open and whenever the store ticks — the dialog adds through the
  // same store, so an attach made there lands here without a second signal.
  useEffect(() => {
    if (open) setDocs(listDocs())
  }, [open, rev])

  if (!open) return null

  const drop = (id: string, title: string) => {
    removeDoc(id)
    setDocs(listDocs())
    useStore.getState().toast(`Dropped · ${title}`, 'info')
  }

  return (
    <Floating>
      <div style={{ position: 'fixed', inset: 0, zIndex: 59 }} onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-label="What this run may draw on"
        style={{
          position: 'fixed',
          bottom: (anchor?.up ?? 0) + 14,
          left: anchor?.left ?? 0,
          width: 318,
          maxWidth: 'calc(100vw - 32px)',
          padding: 8,
          borderRadius: 'var(--r-glass)',
          ...glass(),
          animation: 'pillDrop 0.2s cubic-bezier(0.22, 1, 0.36, 1) both',
          zIndex: 'var(--z-modal)' as unknown as number,
        }}
      >
        <div className="srcpop-head">What this run may draw on</div>

        {docs.map((d) => (
          <div key={d.id} className="srcpop-row">
            <span className="srcpop-ext">{docExt(d.title)}</span>
            <span className="srcpop-body">
              <span className="srcpop-name">{d.title}</span>
              <span className="srcpop-meta">
                {d.passages} passage{d.passages === 1 ? '' : 's'} · {d.chars.toLocaleString()} chars
              </span>
            </span>
            <button
              type="button"
              className="srcpop-drop"
              title="Remove"
              onClick={() => drop(d.id, d.title)}
            >
              <Icon name="close" size={9} />
            </button>
          </div>
        ))}

        {docs.length === 0 && (
          <div className="srcpop-empty">
            Nothing private attached. The run reasons from the graph and public sources only.
          </div>
        )}

        {/* The hint is what the dialog can actually take, which is the only rule
            this line has ever had — there was no file input until the corpus dialog
            grew one, and until then this row named formats nothing would accept. It
            now opens a dialog that reads a chosen or dropped .txt, .md or .pdf as
            well as a paste, so the dashed target is finally the drop-zone idiom it
            always looked like. Still "Add" and not "Attach": nothing uploads, the
            file is read on this machine, and the text waits in a box to be read
            before anything is stored. */}
        <button type="button" className="srcpop-add" onClick={onOpenDialog}>
          <span>Add a document</span>
          <span className="srcpop-formats">TXT · MD · PDF</span>
        </button>

        <div className="srcpop-note">
          Private sources stay in your workspace, are cited by filename, and outrank anything the
          model recalls on its own.
        </div>
      </div>
    </Floating>
  )
}

/**
 * The four dials, in one panel IN FLOW above the run strip: lens, temper, moves
 * and the spend cap. They write the same store the tune panel writes — one dial,
 * two handles — and every figure under the cap is the tune panel's, handed down
 * as `CapFacts`, so the two surfaces that set a cap cannot describe it two ways.
 *
 * This was four chips under the input, each opening its own popover over the
 * map, and on a phone the four folded behind an Adjust press (D158). The study
 * counted four popovers and a floating pod for one run, so the desktop takes the
 * phone's Adjust too, and what it opens is one panel in the composer's own flow:
 * D22's rule, that the dials never float over the field — least of all over a
 * Stop, which in a popover over the map they could. In flow they push the strip
 * up instead of covering anything, and the bar never yields its height.
 *
 * Each dial is a radiogroup of buttons, not a menu: Tab, ↵ and Space keep
 * working and a reader still hears "not selected, 2 of 7". The line under each
 * group glosses the choice IN FORCE — seven near-identical lens names are the
 * one choice here that needs its gloss beside it.
 */
export function AdjustPanel({ cap: capFacts }: { cap?: CapFacts }) {
  const lens = useStore((s) => s.lens)
  const setLens = useStore((s) => s.setLens)
  const temper = useStore((s) => s.temper)
  const setTemper = useStore((s) => s.setTemper)
  const runTune = useStore((s) => s.runTune)
  const setRunTune = useStore((s) => s.setRunTune)
  const runCeiling = useStore((s) => s.runCeiling)
  const setRunCeiling = useStore((s) => s.setRunCeiling)
  const busy = useStore((s) => s.busy)
  const runLive = useStore((s) => s.run !== null && s.run.phase !== 'ended')
  const ids = useId()
  // Escape puts the panel down before the global ladder acts: the panel is a
  // layer somebody opened, and the ladder's next rung would deselect the node
  // under it. Mounted only while open, so this listens only then.
  useEscapeToClose(true, () => setAdjustOpen(false))

  const cap = Number.parseFloat(runCeiling)
  const capSet = Number.isFinite(cap) && cap > 0
  // The cap is not a dial of the pass in flight: a run's ceiling is read at its
  // start, and a lone pass is priced against the cap at its press.
  const lockCap = busy || runLive

  // "Unattended" is the word that earns the moves dial its meaning, and it is
  // exactly the word ask-first mode makes false — the same figure heads a run
  // that will hold at every move for a go.
  const movesHead =
    runTune.mode === 'confirm' ? 'How far to run, asking first' : 'How far to run unattended'

  return (
    <div className="adjust" role="group" aria-label="The next run’s dials">
      <AdjustDial id={`${ids}-lens`} label="Lens" name="How to reason" hint={LENS_HINT[lens]}>
        {(Object.keys(LENS_LABEL) as Lens[]).map((l) => (
          <AdjustOpt key={l} on={l === lens} onClick={() => setLens(l)}>
            {LENS_LABEL[l]}
          </AdjustOpt>
        ))}
      </AdjustDial>

      <AdjustDial
        id={`${ids}-temper`}
        label="Temper"
        name="How far to reach"
        hint={TEMPER_HINT[temper]}
      >
        {TEMPERS.map((t) => (
          <AdjustOpt key={t.id} on={t.id === temper} onClick={() => setTemper(t.id)}>
            {t.name}
          </AdjustOpt>
        ))}
      </AdjustDial>

      {/* One gloss, not one per option: the word that has to be learned is
          MOVE, and 12, 24 and 48 are the same thing three times. The figure in
          force is always among the options, so a typed /moves 30 that was
          committed is checked rather than leaving the group with nothing on. */}
      <AdjustDial
        id={`${ids}-moves`}
        label="Moves"
        name={movesHead}
        hint={
          runTune.execution === 'portfolio'
            ? 'Moves stay logical: up to three compatible angles on one node can share a model pass. It stops at the move budget, the frontier, or your hand.'
            : 'One move is one model pass. It stops there, when the frontier runs out, or when you stop it.'
        }
      >
        {withCurrent([...MOVE_ROWS], runTune.moves).map((n) => (
          <AdjustOpt
            key={n}
            on={n === runTune.moves}
            onClick={() => setRunTune({ ...runTune, moves: n })}
          >
            <span className="num">{n}</span>
          </AdjustOpt>
        ))}
      </AdjustDial>

      {/* The cap. Below the pass floor a preset cannot start a run, so it is
          greyed and the line under the group says the figure it would take and
          the model that set it, in words (D154); between the floor and a check's
          own reserve it runs, reasoning only, and says so on its face. The
          stepper stays OUTSIDE the group: it is a spinner over a continuous
          amount, not a sixth choice among the five, and a typed amount checks
          none of them — which is the truth. */}
      <AdjustDial id={`${ids}-cap`} label="Cap" name="Spend cap for the next run">
        {CAP_PRESETS.map((v) => {
          const on = v === null ? !capSet : capSet && cap === v
          const { under, hint } = capPresetHint(v, capFacts)
          return (
            <AdjustOpt
              key={String(v)}
              on={on}
              disabled={lockCap || under}
              // The hint is part of the choice, so a reader hears it too.
              label={v === null ? 'No cap' : `${fmtUsd(v)}${hint ? `, ${hint}` : ''}`}
              onClick={() => setRunCeiling(v === null ? '' : String(v))}
            >
              {v === null ? 'No cap' : <span className="num">{fmtUsd(v)}</span>}
              {hint && !under && <span className="adjust-opt-hint">{hint}</span>}
            </AdjustOpt>
          )
        })}
      </AdjustDial>
      <div className="adjust-dial">
        <span className="adjust-key" aria-hidden />
        <span className="adjust-step" role="group" aria-label="Custom cap">
          <span className="adjust-step-key" aria-hidden>
            Custom
          </span>
          <button
            className="btn btn--icon"
            aria-label="Lower the cap"
            disabled={lockCap}
            onClick={() => setRunCeiling(stepCapAmount(runCeiling, -1))}
          >
            <Icon name="minus" size={13} />
          </button>
          <span className="num adjust-step-value">{capSet ? fmtUsd(cap) : '—'}</span>
          <button
            className="btn btn--icon"
            aria-label="Raise the cap"
            disabled={lockCap}
            onClick={() => setRunCeiling(stepCapAmount(runCeiling, 1))}
          >
            <Icon name="plus" size={13} />
          </button>
        </span>
        {/* The tune panel's two sentences, here where the cap is chosen: the
            floor and its model, then what the cap in force would buy. A live
            region, because the second changes under the stepper and the preset
            a reader just pressed is what it describes. */}
        {capFacts && capFloorLine(capFacts) && (
          <div className="adjust-hint adjust-cap-lines" role="status" aria-live="polite">
            <span>{capFloorLine(capFacts)}</span>
            {capSet && capRangeLine(capFacts, cap) && (
              <span data-under={(capFacts.floor && cap < capFacts.floor.usd) || undefined}>
                {capRangeLine(capFacts, cap)}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/** One dial: its key, its options as one radiogroup named for what it decides,
 *  and the gloss of the choice in force. */
function AdjustDial({
  id,
  label,
  name,
  hint,
  children,
}: {
  id: string
  label: string
  name: string
  hint?: string
  children: ReactNode
}) {
  return (
    <div className="adjust-dial">
      <span className="adjust-key" id={id} title={name}>
        {label}
      </span>
      <div className="adjust-opts" role="radiogroup" aria-label={name}>
        {children}
      </div>
      {hint && <p className="adjust-hint">{hint}</p>}
    </div>
  )
}

/** One option. The mark is `aria-checked`; the fill says the same thing in ink. */
function AdjustOpt({
  on,
  disabled,
  label,
  onClick,
  children,
}: {
  on: boolean
  disabled?: boolean
  label?: string
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      className="adjust-opt"
      role="radio"
      aria-checked={on}
      aria-label={label}
      data-on={on || undefined}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

/* ── Small pieces ─────────────────────────────────────────────────────── */

/**
 * One `/command` completion.
 *
 * The pointer's row and the arrows' row are tracked apart and lit the same. Not
 * merged, because merging means hover arms ↵ — and a cursor resting over this
 * list while someone types is not a choice they made.
 */
function CompletionRow({
  id,
  name,
  hint,
  theme,
  active,
  onPick,
}: {
  id: string
  name: string
  hint: string
  theme: Theme
  active: boolean
  onPick: (name: string) => void
}) {
  const [hov, bind] = useHover()
  const lit = hov || active
  return (
    <button
      {...bind}
      id={id}
      type="button"
      role="option"
      aria-selected={active}
      onClick={() => onPick(name)}
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 8,
        width: '100%',
        padding: '4px 8px',
        borderRadius: 8,
        background: hoverFill(theme, lit),
        color: lit ? 'var(--text-1)' : 'var(--text-2)',
        fontSize: '11.5px',
        textAlign: 'left',
        transition: 'background 130ms ease, color 130ms ease',
      }}
    >
      <b className="num" style={{ color: 'var(--accent)', flex: 'none' }}>
        /{name}
      </b>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {hint}
      </span>
    </button>
  )
}

/** Set by a brief chip — the one chip, or a directive's own — read once by the
 *  tune panel as it mounts: open it AT the Standing brief section, which sits
 *  below the dials and is where a directive is reworded. */
let briefAsked = false

function Chip({
  label,
  title,
  onClear,
  onOpen,
}: {
  label: string
  title?: string
  onClear?: () => void
  /** The chip itself is a press — the brief's one chip opens its list. */
  onOpen?: () => void
}) {
  const [hov, bind] = useHover()
  return (
    <span
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        // Symmetric when there is no ✕ to make room for: the one clearless chip
        // is a fact about the exploration, not a control that lost its button.
        padding: onClear ? '3px 5px 3px 9px' : '3px 9px',
        borderRadius: 999,
        fontSize: 'var(--fs-micro)',
        color: 'var(--accent-ink)',
        // Deliberately NOT the plain --glass-fill, and the exception is narrow:
        // this is an accent pill, and it has to read as a tint, so it takes
        // the accent. Nothing else in the app may opt out. The tint is mixed
        // INTO the floating material rather than laid over whatever is behind
        // the pill, though: opaque, as every surface is since D358, so above a
        // floating bar the graph does not show through it — which, with the
        // blur it wore until then, is what made it a little pane of glass.
        background: 'color-mix(in srgb, var(--accent) 14%, var(--glass-fill))',
        border: '1px solid color-mix(in srgb, var(--accent) 26%, transparent)',
        maxWidth: 320,
      }}
    >
      {onOpen ? (
        <button
          type="button"
          className="lnk"
          onClick={onOpen}
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: 'inherit',
            font: 'inherit',
          }}
        >
          {label}
        </button>
      ) : (
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </span>
      )}
      {onClear && (
        <button
          {...bind}
          type="button"
          aria-label={`Remove ${title ?? label}`}
          onClick={onClear}
          style={{
            display: 'grid',
            placeItems: 'center',
            width: 14,
            height: 14,
            flex: 'none',
            borderRadius: '50%',
            color: 'inherit',
            background: hov ? 'color-mix(in srgb, var(--accent) 25%, transparent)' : 'transparent',
            opacity: hov ? 1 : 0.6,
            fontSize: '13px',
            lineHeight: 1,
          }}
        >
          ×
        </button>
      )}
    </span>
  )
}

function SegBtn({
  label,
  on,
  onClick,
  theme,
}: {
  label: string
  on: boolean
  onClick: () => void
  theme: Theme
}) {
  const [hov, bind] = useHover()
  return (
    <button
      {...bind}
      role="tab"
      aria-selected={on}
      onClick={onClick}
      style={{
        flex: 1,
        height: 28,
        borderRadius: 8,
        fontSize: '11.5px',
        fontWeight: on ? 590 : 500,
        color: on ? 'var(--text-1)' : hov ? 'var(--text-1)' : 'var(--text-2)',
        background: on
          ? theme === 'dark'
            ? 'rgba(255,255,255,0.1)'
            : 'rgba(48,40,28,0.1)'
          : hoverFill(theme, hov),
        boxShadow: on
          ? theme === 'dark'
            ? 'inset 0 1px 0 rgba(255,255,255,0.12)'
            : 'inset 0 1px 0 rgba(255,255,255,0.7)'
          : 'none',
        transition: 'background 130ms ease, color 130ms ease',
      }}
    >
      {label}
    </button>
  )
}

function SegRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={EYEBROW}>{label}</span>
      <div role="tablist" aria-label={label} style={{ display: 'flex', gap: 3 }}>
        {children}
      </div>
    </div>
  )
}

/* ── The tune panel ───────────────────────────────────────────────────── */

function TunePanel({
  theme,
  budget,
  auditorName,
  auditorProvider,
  ceilingFloor,
  checkFloor,
  ceilingTooLow,
  ceilingSet,
  ceilingUsd,
  runEst,
  runMovesEst,
  voice,
  runModels,
  spec,
  live,
  moveRange,
  steerChips,
  onClearSteer,
  rev,
  onRun,
  runnable,
  runTitle,
  floorUnit,
}: {
  theme: Theme
  budget: ReturnType<typeof budgetFor>
  auditorName: string | null
  auditorProvider: string | null
  /**
   * The smallest ceiling that guarantees one pass, AND the model it came from,
   * as one value. Two props would be two facts that can disagree, which is the
   * whole reason `dearestPass` returns the pair: the floor is a max over every
   * routable role, so the model that set it is frequently not the selection, and
   * a sentence that names the selection sends a reader to re-point a model that
   * was affordable.
   */
  ceilingFloor: { entry: { name: string }; usd: number; seats: number } | null
  /** A source check's own reserve, when the run schedules checks — see
   *  `ceilingFloors`. A check runs only while the cap has this much left. */
  checkFloor: { entry: { name: string }; usd: number } | null
  /** What that figure bounds, in words — "a single pass", or one panel and its
   *  seats. Passed rather than re-derived so the warning and the button's title
   *  cannot come to describe the same floor differently. */
  floorUnit: string
  ceilingTooLow: boolean
  ceilingSet: boolean
  ceilingUsd: number | null
  /** What the press is quoted at: the moves, and the voice's wrap-up when on. */
  runEst: number | null
  /** The moves alone, which a ceiling is compared against: the wrap-up yields. */
  runMovesEst: number | null
  /** Whether the voice is on, so the figure can say what it includes. */
  voice: boolean
  runModels: ReturnType<typeof runModelsFor>
  spec: RunSpec
  live: { ceiling: string; checks: number | 'every' | undefined }
  moveRange: { fewest: number; likely: number } | null
  /** The committed chips the panel's own dials do not restate — see steerChips.ts. */
  steerChips: CommittedChip[]
  /** The composer's one dispatcher for a committed chip's removal — passed down
   *  so the panel and the bar cannot come to un-set the same chip differently. */
  onClearSteer: (c: CommittedClear) => void
  rev: number
  onRun: () => void
  runnable: boolean
  runTitle: string
}) {
  const session = useStore((s) => s.session)
  const lens = useStore((s) => s.lens)
  const temper = useStore((s) => s.temper)
  const setLens = useStore((s) => s.setLens)
  const setTemper = useStore((s) => s.setTemper)
  const setTune = useStore((s) => s.setTune)
  // Opened by a brief chip: the list is below the dials, so the panel
  // brings it into view rather than opening on the lens.
  const briefRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!briefAsked) return
    briefAsked = false
    briefRef.current?.scrollIntoView({ block: 'nearest' })
  }, [])
  const runTune = useStore((s) => s.runTune)
  const setRunTune = useStore((s) => s.setRunTune)
  const runCeiling = useStore((s) => s.runCeiling)
  const setRunCeiling = useStore((s) => s.setRunCeiling)
  const busy = useStore((s) => s.busy)
  const liveRun = useStore((s) => s.run !== null && s.run.phase !== 'ended')
  const sourcesOn = useStore((s) => s.sourcesOn)
  const preferCorpus = useStore((s) => s.preferCorpus)
  const entry = useActiveEntry()
  const passReference = projectModelPasses(budget, spec.execution)
  const [closeHov, closeBind] = useHover()

  /**
   * The unpriced half of what a source check sends, under the toggles in force.
   *
   * The sentence below is a promise about what leaves the machine, not about what
   * the run is charged for, so it has to carry both classes: the provider's own
   * searches and page fetches — `SEARCHES_PER_CHECK`, the only half with a rate —
   * and the research rows' keyless lookups. It kept the first alone while the
   * check grew four rows, a retraction lookup and two full texts, which made the
   * figure an under-count of exactly the requests nobody is billed for.
   *
   * Zero under "Prefer my sources over the web", because `runVerify` suppresses
   * the whole gather there rather than narrowing it: the toggle says the web, and
   * four public APIs are the web.
   */
  const researchCeiling = preferCorpus ? 0 : liveLookupsPerCheck(sourcesOn)

  /**
   * Whether a source check has anything to read on the route in force: a search,
   * or the person's own documents under “Prefer my sources”.
   *
   * Asked through `checkCanRead` because it is the run's own question — `runVerify`
   * refuses on it and `startRun` switches an ordinary run's checks off on it (D293)
   * — and the sentence below promises what the run will do. Asked of the route's
   * search alone, it promised “all of them reasoning” over a run that checks the
   * person's documents (D294).
   */
  const checkReads = useStore(checkCanRead)

  const patch = (p: Partial<RunTune>) => setRunTune({ ...runTune, ...p })

  const stepCeiling = (dir: 1 | -1) => setRunCeiling(stepCapAmount(live.ceiling, dir))

  const tally = useMemo(() => {
    let contradictions = 0
    let gaps = 0
    let insights = 0
    for (const id of session.order) {
      const n = session.nodes[id]
      if (!n || n.parentId === null) continue
      const ch = kindChannelOf(n)
      if (ch === 'tension') contradictions++
      else if (ch === 'gap') gaps++
      else if (ch === 'insight') insights++
    }
    return { total: Math.max(0, session.order.length - 1), contradictions, gaps, insights }
  }, [session, rev])

  const ledger = useMemo(() => estimateSpendUsd(session.spend), [session, rev])

  const untilShort = useMemo(() => {
    const u = budget.until
    if (!u) return null
    if ((u.kind === 'refuted' || u.kind === 'supported') && budget.checks < u.n) {
      return `/until counts source-check readings, and ${u.n} ${u.kind} cannot arrive from ${budget.checks} check${budget.checks === 1 ? '' : 's'} — add /check ${u.n} or more.`
    }
    if (u.kind === 'split' && (budget.panels ?? 0) < u.n) {
      return `/until counts split panels, and ${u.n} cannot arrive from ${budget.panels ?? 0} — add /panel ${u.n}.`
    }
    if (u.kind === 'objection' && (budget.audits ?? 0) < 1) {
      return '/until counts objections from a cross-examination, and this run makes none — add /audit.'
    }
    return null
  }, [budget])

  return (
    <div
      className="scroll"
      role="dialog"
      aria-label="Tune the next run"
      style={{
        ...glass(),
        width: 'min(430px, 100%)',
        maxHeight: 'min(540px, 62vh)',
        overflowY: 'auto',
        borderRadius: 'var(--r-glass)',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        pointerEvents: 'auto',
        textAlign: 'left',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          style={{
            fontSize: '13.5px',
            fontWeight: 590,
            letterSpacing: '-0.014em',
            color: 'var(--text-1)',
          }}
        >
          Tune the next run
        </span>
        <div style={{ flex: 1 }} />
        <button
          {...closeBind}
          onClick={() => setTune(false)}
          aria-label="Close"
          style={{
            display: 'grid',
            placeItems: 'center',
            width: 26,
            height: 26,
            borderRadius: 8,
            color: closeHov ? 'var(--text-1)' : 'var(--text-3)',
            background: hoverFill(theme, closeHov),
            transition: 'background 130ms ease, color 130ms ease',
          }}
        >
          <Icon name="close" size={13} />
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={EYEBROW}>Lens</span>
        <select
          className="select"
          value={lens}
          aria-label="Lens"
          onChange={(e) => setLens(e.target.value as Lens)}
        >
          {(Object.keys(LENS_LABEL) as Lens[]).map((l) => (
            <option key={l} value={l}>
              {LENS_LABEL[l]}
            </option>
          ))}
        </select>
        <p style={NOTE}>{LENS_HINT[lens]}</p>
      </div>

      {/* The note under the tabs, not inside them — `SegRow` puts its children
          in the tablist, and a paragraph is not a tab. The wrapper is the shape
          Cross-examinations below already uses. It matches the Lens field
          directly above and closes the last place Temper appeared naked. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <SegRow label="Temper">
          {TEMPERS.map((t) => (
            <SegBtn
              key={t.id}
              label={t.name}
              on={temper === t.id}
              onClick={() => setTemper(t.id)}
              theme={theme}
            />
          ))}
        </SegRow>
        <p style={NOTE}>{TEMPER_HINT[temper]}</p>
      </div>

      {(session.brief?.length ?? 0) > 0 && (
        <div ref={briefRef} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={EYEBROW}>
            Standing brief · {session.brief!.length} of {MAX_BRIEF}
          </span>
          {/* One row a directive, with Edit and ✕ — see `BriefList`. */}
          <BriefList session={session} />
          <p style={NOTE}>
            Every pass from here on is briefed with these. Givens are stipulated, never certified.
            Rewording or removing one is undoable, and the passes already made keep the words they
            were sent.
          </p>
        </div>
      )}

      {/* The display switches — what is drawn — live on the tool dock's eye
          popover now: they change how the canvas looks, not what the next run
          does, and this panel is about the run. */}

      <SegRow label="Moves">
        {withCurrent([...MOVE_ROWS], budget.moves).map((n) => (
          <SegBtn
            key={n}
            label={String(n)}
            on={budget.moves === n}
            onClick={() => patch({ moves: n })}
            theme={theme}
          />
        ))}
      </SegRow>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <SegRow label="Mode">
          <SegBtn
            label="On its own"
            on={(runTune.mode ?? 'auto') === 'auto'}
            onClick={() => patch({ mode: 'auto' })}
            theme={theme}
          />
          <SegBtn
            label="Ask first"
            on={runTune.mode === 'confirm'}
            onClick={() => patch({ mode: 'confirm' })}
            theme={theme}
          />
        </SegRow>
        <p style={NOTE}>
          Ask first proposes each move, priced, and waits for your go — the run holds between passes
          for as long as you do. On its own is the run you can walk away from.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <SegRow label="Execution">
          <SegBtn
            label="Portfolio"
            on={(runTune.execution ?? DEFAULT_EXECUTION) === 'portfolio'}
            onClick={() => patch({ execution: 'portfolio' })}
            theme={theme}
          />
          <SegBtn
            label="Sequential"
            on={runTune.execution === 'sequential'}
            onClick={() => patch({ execution: 'sequential', detail: 'full' })}
            theme={theme}
          />
        </SegRow>
        <p style={NOTE}>
          Portfolio keeps the move budget intact but sends two or three compatible angles on one
          node in a shared model pass, so a run makes about a third as many reasoning requests.
          Grounding passes stay separate. Sequential is one move per request, the run this app
          shipped with.
        </p>
      </div>

      {runTune.execution === 'portfolio' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <SegRow label="Leaf detail">
            <SegBtn
              label="Full"
              on={(runTune.detail ?? 'full') === 'full'}
              onClick={() => patch({ detail: 'full' })}
              theme={theme}
            />
            <SegBtn
              label="Progressive · experimental"
              on={runTune.detail === 'progressive'}
              onClick={() => patch({ detail: 'progressive' })}
              theme={theme}
            />
          </SegRow>
          <p style={NOTE}>
            Progressive writes a complete reasoning kernel for new leaves and adds detail, in the
            same pass, when a later run move develops that leaf.
          </p>
        </div>
      )}

      <SegRow label="Source checks">
        {/* `withCurrent`, by hand, because this is the one row whose values mix
            numbers with words: a committed `/check 3` must be visible and
            selected here like an odd move count is on Moves — not a row of
            three unselected segments over a count only the spend paragraph
            whispers. */}
        {[
          undefined,
          8,
          'every' as const,
          ...(typeof live.checks === 'number' && live.checks !== 8 ? [live.checks] : []),
        ].map((c) => (
          <SegBtn
            key={String(c)}
            label={
              c === undefined
                ? 'Default'
                : c === 'every'
                  ? 'Every claim'
                  : c === 8
                    ? 'More'
                    : String(c)
            }
            on={live.checks === c}
            onClick={() => patch({ checks: c })}
            theme={theme}
          />
        ))}
      </SegRow>

      <SegRow label="Panels">
        {withCurrent([0, 1, 3], budget.panels ?? 0).map((n) => (
          <SegBtn
            key={n}
            label={n === 0 ? 'None' : String(n)}
            on={(budget.panels ?? 0) === n}
            onClick={() => patch({ panels: n })}
            theme={theme}
          />
        ))}
      </SegRow>

      {auditorName ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <SegRow label="Cross-examinations">
            {withCurrent([0, 1, 2], budget.audits ?? 0).map((n) => (
              <SegBtn
                key={n}
                label={n === 0 ? 'None' : String(n)}
                on={(budget.audits ?? 0) === n}
                onClick={() => patch({ audits: n })}
                theme={theme}
              />
            ))}
          </SegRow>
          <p style={NOTE}>
            On {auditorName}, from another company — which is the whole point, and which means these
            passes bill at its rates and not at {entry.name}&rsquo;s.
          </p>
        </div>
      ) : (
        <p style={NOTE}>{CROSS_EXAM_NEEDS_SECOND_KEY}</p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <SegRow label="Insight passes">
          {withCurrent([0, 1, 2], budget.conjectures ?? 0).map((n) => (
            <SegBtn
              key={n}
              label={n === 0 ? 'None' : String(n)}
              on={(budget.conjectures ?? 0) === n}
              onClick={() => patch({ conjectures: n === 0 ? undefined : n })}
              theme={theme}
            />
          ))}
        </SegRow>
        <p style={NOTE}>
          Off unless you turn it on. Each is a pass proposing claims no source the model knows
          states, aimed where the graph&rsquo;s records of what does not fit gather, and each
          insight that lands is searched for prior art in one of the run&rsquo;s check slots.
        </p>
      </div>

      {ceilingFloor !== null ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={EYEBROW}>Stop at</span>
          {/* Presets first, the typed amount as the custom case. The cap is read
              far beyond the run control now — the spend meter, the CAP hairline
              on the step rail, and every price-on-hover compare against it. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
            {CAP_PRESETS.map((v) => {
              // Below the floor a preset cannot start a run — the Budget
              // popover's rule, for the same figures.
              const under = v !== null && v < ceilingFloor.usd
              return (
                <button
                  key={String(v)}
                  className="btchip"
                  data-on={
                    (v === null
                      ? live.ceiling.trim() === ''
                      : Number.parseFloat(live.ceiling) === v) || undefined
                  }
                  title={
                    under
                      ? `from ${fmtUsd(ceilingFloor.usd)} est. on ${ceilingFloor.entry.name}`
                      : undefined
                  }
                  style={under ? { opacity: 0.45 } : undefined}
                  disabled={busy || liveRun || runCeiling !== live.ceiling || under}
                  onClick={() => setRunCeiling(v === null ? '' : String(v))}
                >
                  {v === null ? 'No cap' : fmtUsd(v)}
                </button>
              )
            })}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button
              className="btn btn--icon"
              aria-label="Lower the cap"
              disabled={busy || liveRun || runCeiling !== live.ceiling}
              onClick={() => stepCeiling(-1)}
            >
              <Icon name="minus" size={13} />
            </button>
            <div className="amount" style={{ flex: '1 1 auto' }}>
              <span className="amount-affix" aria-hidden="true">
                $
              </span>
              <input
                className="input num"
                inputMode="decimal"
                value={live.ceiling}
                placeholder="no ceiling"
                aria-label="Stop the run at this estimated spend, in dollars"
                onChange={(e) => setRunCeiling(cleanAmount(e.target.value))}
                disabled={busy || liveRun || runCeiling !== live.ceiling}
                title={
                  runCeiling !== live.ceiling
                    ? 'A /spend in the box is setting this. Remove its chip to type one here.'
                    : undefined
                }
              />
            </div>
            <button
              className="btn btn--icon"
              aria-label="Raise the cap"
              disabled={busy || liveRun || runCeiling !== live.ceiling}
              onClick={() => stepCeiling(1)}
            >
              <Icon name="plus" size={13} />
            </button>
          </div>
        </div>
      ) : (
        <p style={NOTE}>
          No spending ceiling on {entry.name}: what your own endpoint charges is between you and
          whoever runs it, so there is no rate here to hold a run to. The move count is the bound.
        </p>
      )}

      {steerChips.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={EYEBROW}>Steering</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {steerChips.map((c) => {
              const cl = c.clear
              return (
                <Chip
                  key={c.key}
                  label={c.label}
                  title={c.title}
                  onClear={cl ? () => onClearSteer(cl) : undefined}
                />
              )
            })}
          </div>
        </div>
      )}

      {untilShort && <p style={{ ...NOTE, color: 'var(--ink-refuted)' }}>{untilShort}</p>}

      {/* What the next press will spend, said before it is pressed. Ported from
          the old rail — the words are the contract, so they travelled intact. */}
      <p style={NOTE}>
        {spec.execution === 'portfolio'
          ? `${budget.moves} logical moves on ${runModels.reason.name}; compatible reasoning angles share one pass in groups of up to three. If packets stay full and every planned grounding slot finds work, the reference mix is about ${passReference} model passes; graph shape, eligibility, stalls, or fallback can move the actual count — `
          : `${budget.moves} model passes on ${runModels.reason.name} — `}
        {!checkReads
          ? `all of them reasoning, since a source check needs a search ${
              runModels.check.id === runModels.reason.id
                ? 'this endpoint may not have'
                : `${runModels.check.name} may not have`
            }. The last is a synthesis scan.`
          : runModels.check.search === 'none'
            ? /* Checks with the documents and nothing else to read: “Prefer my
                 sources” on a route with no search. No figure, because there are
                 no requests to count — the toggle skips the research rows, a pass
                 that reads only documents declares no web tool, and a claim they
                 do not address is refused, having no web to fall back to. A number
                 built from SEARCHES_PER_CHECK here would promise requests the
                 check cannot make. */
              `up to ${budget.checks} of them source checks${
                runModels.check.id === runModels.reason.id ? '' : ` on ${runModels.check.name}`
              }, which read only your own documents and make no live web requests, since ${
                runModels.check.id === runModels.reason.id ? 'this endpoint' : 'it'
              } may not have a search. The last is a synthesis scan.`
            : /* The routing clause moved onto the CHECKS, where it was always true.
                 It sat on the request count while that count was the check model's
                 searches and nothing else; now that the figure also carries the
                 research rows' lookups, "N live web requests on GPT-5" would put a
                 public index's traffic on a provider's name. */
              `up to ${budget.checks} of them source checks${
                runModels.check.id === runModels.reason.id ? '' : ` on ${runModels.check.name}`
              }, which is at most ${budget.checks * (SEARCHES_PER_CHECK + researchCeiling)} live web requests, and the last a synthesis scan.`}
        {runModels.panel.id !== runModels.reason.id &&
          (budget.panels ?? 0) > 0 &&
          ` Panels run on ${runModels.panel.name}.`}
        {(budget.panels ?? 0) > 0 &&
          ` Up to ${budget.panels} of the moves are panels, and a panel is three passes rather than one.`}
        {(budget.audits ?? 0) > 0 &&
          auditorName &&
          ` Up to ${budget.audits} hand a branch to ${auditorName} to attack, billed by ${auditorProvider}.`}
        {/* The prior-art search is promised only where it can run. It reads the web
            and nothing else, so on a check route with no search it never does:
            with “Prefer my sources” off the checks are off, and with it on the run
            drops the searches and says so (D294). */}
        {(budget.conjectures ?? 0) > 0 &&
          (budget.checks > 0 && runModels.check.search !== 'none'
            ? ` Up to ${budget.conjectures} ${budget.conjectures === 1 ? 'is an insight pass' : 'are insight passes'}, and each insight one lands takes the next check slot for a prior-art search before any source check${
                SEARCHES_PER_PRIOR_ART > SEARCHES_PER_CHECK + researchCeiling
                  ? ` — up to ${SEARCHES_PER_PRIOR_ART} live web requests rather than ${SEARCHES_PER_CHECK + researchCeiling}, so at most ${budget.checks * SEARCHES_PER_PRIOR_ART} across the slots`
                  : ''
              }.`
            : ` Up to ${budget.conjectures} ${budget.conjectures === 1 ? 'is an insight pass' : 'are insight passes'}; ${
                budget.checks > 0
                  ? 'with no web search where source checks go'
                  : 'with no check slots'
              }, what they propose stays unsearched.`)}
        {spec.ladder?.length === 1 &&
          (spec.ladder[0] === 'expand'
            ? ' Every move is expand — the one verb that opens more ends than it closes, so the budget or the graph cap goes first, not the frontier.'
            : ` Every move is ${spec.ladder[0]}, so it will stop on an empty frontier well before the budget.`)}
        {(spec.ladder?.length ?? 0) > 1 && ` Verbs are tried ${spec.ladder!.join(' → ')}.`}
        {spec.depth !== undefined &&
          ` It works ${spec.depth} ring${spec.depth === 1 ? '' : 's'} out rather than 6.`}
        {spec.focus && ' It stays inside the selected branch.'}
        {budget.until &&
          ` It stops the moment it has ${untilLabel(budget.until)} — every other ceiling still binds, so the condition can only end it sooner.`}
        {runEst !== null &&
          ` Up to about ${fmtUsd(runEst)} at list prices read on ${PRICE_READ_ON}${voice ? ', the voice’s summary at the end included' : ''} — an estimate that leans high, not a quote.`}{' '}
        It stops when the budget is spent, when the frontier runs out, or when you stop it. The
        budget usually goes first.
      </p>

      {ceilingTooLow && ceilingFloor !== null && (
        <p style={{ ...NOTE, color: 'var(--ink-refuted)' }}>
          {fmtUsd(ceilingUsd as number)} cannot be guaranteed to cover {floorUnit} on{' '}
          {ceilingFloor.entry.name}, so the run would stop before its first move.{' '}
          {fmtUsd(ceilingFloor.usd)} is the smallest ceiling that can — a bound on the dearest work
          possible, not on a usual one.
        </p>
      )}
      {ceilingSet && !ceilingTooLow && runMovesEst !== null && (
        <p style={NOTE}>
          {(ceilingUsd as number) >= runMovesEst
            ? `Your ${fmtUsd(ceilingUsd as number)} ceiling is above what ${budget.moves} moves could draw, so the move count will go first and the ceiling is there as a backstop.`
            : `Your ${fmtUsd(ceilingUsd as number)} ceiling goes first${
                moveRange
                  ? `, so you will likely get ${
                      moveRange.fewest === moveRange.likely
                        ? moveRange.likely
                        : `${moveRange.fewest}–${moveRange.likely}`
                    } of the ${budget.moves} moves — projected from the same list prices, not promised`
                  : `, so some of the ${budget.moves} moves will go unspent`
              }. It stops BEFORE the pass that would cross it rather than after, so it ends a little under — the shortfall is what makes the ceiling a ceiling.`}
          {voice &&
            ' The voice’s summary at the end yields to the moves: where the run leaves it no room under the ceiling, the run ends without one.'}
        </p>
      )}
      {/* A check's own reserve, said where a cap is set rather than discovered in
          the account. Every pass is held back by what it could draw, and a check
          can draw several reasoning passes' worth, so a cap under a check's
          reserve still runs — reasoning — rather than refusing (`ceilingFloors`). */}
      {ceilingSet && !ceilingTooLow && checkFloor !== null && (
        <p style={NOTE}>
          {(ceilingUsd as number) <= checkFloor.usd
            ? `No source check fits under it: each one is held to ${fmtUsd(checkFloor.usd)} on ${checkFloor.entry.name}, so this run reasons, skips its checks, and its account says so.`
            : `A source check runs only while ${fmtUsd(checkFloor.usd)} of it is left — what one check on ${checkFloor.entry.name} could draw. After that the run keeps reasoning, and its account says which checks it skipped.`}
        </p>
      )}

      {/* The autonomous run's press, directly under the sentences that say what
          it will spend. The same `letItRun` as the strip's Run, so it says the
          strip's word and the strip's count: it read "Run the open ends", a
          third name for one press beside the strip's "Let it run · Run" and the
          palette's row (D338). */}
      <button
        onClick={onRun}
        disabled={!runnable}
        title={runTitle}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          height: 38,
          borderRadius: 'var(--r-md)',
          fontSize: '12.5px',
          fontWeight: 640,
          letterSpacing: '-0.014em',
          color: theme === 'dark' ? '#0a0c12' : '#fff',
          background: theme === 'dark' ? 'linear-gradient(180deg, #ffffff, #e6eaf3)' : '#1c1a16',
          opacity: runnable ? 1 : 0.38,
          cursor: runnable ? 'pointer' : 'not-allowed',
        }}
      >
        <Icon name="play" size={13} />
        Run · {budget.moves} moves
      </button>

      {/* This exploration, in numbers, and what its passes have drawn so far.
          "Insights" counts the insight CHANNEL, which insights, takeaways and
          syntheses all wear (`kindChannelOf`), so it can stand above a fold of
          fewer insight cards; its hover says what it counts (D333). */}
      <div
        style={{
          display: 'flex',
          gap: 14,
          paddingTop: 10,
          borderTop:
            theme === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(48,40,28,0.1)',
        }}
      >
        {(
          [
            ['Nodes', tally.total, 'var(--text-3)', 'Nodes'],
            ['Insights', tally.insights, 'var(--sem-insight)', 'Insights, takeaways and syntheses'],
            ['Contradictions', tally.contradictions, 'var(--sem-tension)', 'Contradictions'],
            ['Gaps', tally.gaps, 'var(--sem-gap)', 'Gaps'],
          ] as const
        ).map(([label, n, tint, title]) => (
          <span
            key={label}
            title={title}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <i style={{ width: 5, height: 5, borderRadius: '50%', background: tint }} />
            <b
              className="num"
              style={{ fontSize: '12.5px', fontWeight: 590, color: 'var(--text-1)' }}
            >
              {n}
            </b>
            <span style={{ fontSize: 'var(--fs-micro)', color: 'var(--text-3)' }}>{label}</span>
          </span>
        ))}
      </div>

      {session.spend.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {session.spend.map((row) => {
            // Counts the provider reported, never a second dollar figure — see
            // `cacheFacts`. Absent on a row recorded before the meter existed,
            // which is why this renders nothing rather than a zero.
            const cache = cacheFacts(row)
            return (
              <div key={`${row.model.provider}/${row.model.id}`}>
                <p className="num" style={{ ...NOTE, textAlign: 'center' }}>
                  {row.model.name} · {tok(row.inputTokens)} in · {tok(row.outputTokens)} out
                  {row.searches > 0 && ` · ${row.searches} search${row.searches === 1 ? '' : 'es'}`}
                </p>
                {cache && (
                  <p
                    className="num"
                    style={{ ...NOTE, textAlign: 'center' }}
                    title={
                      `What the provider said this model's input was made of, and what the estimate below prices each part at: a cache read at this model's read rate, a write at base with its premium on top, and the rest at base. On a complete breakdown that meets the list charge, whatever the mix — a cold start, a changed prefix, an entry that expired before it was reused; input with no breakdown is charged at base, which can only put the figure above it.` +
                      (cache.unclassified > 0
                        ? ` ${tok(cache.unclassified)} more input tokens carry no breakdown, either recorded before this was measured or reported without one, and the share above is not taken over them.`
                        : '')
                    }
                  >
                    {/* "measured" is load-bearing whenever part of the row has no
                        breakdown: the share is taken over what this row could
                        categorise, and a bare "of input" would claim the whole
                        row was measured when it was not. */}
                    {cache.cachedPct}% of {cache.unclassified > 0 ? 'measured ' : ''}input from
                    cache · {tok(cache.cached)} read · {tok(cache.written)} written
                  </p>
                )}
              </div>
            )
          })}
          <CacheReadingLine session={session} />
          {ledger.usd > 0 && (
            <p
              style={{ ...NOTE, textAlign: 'center' }}
              // Says what the cache line directly above it says, because this PR
              // put the two side by side. Both said the figure could sit UNDER
              // the charge on a write-heavy pass, which was true while writes
              // were priced at base; `estimateUsd` charges their premium now, so
              // the unconditional promise is the true one again and both say it.
              title={`List prices read on ${PRICE_READ_ON}. Each part of the input is charged at the rate it bills at — a cache read at the read rate, a cache write with its premium on top — and a page opened for nothing where the provider charges nothing for one. Input or requests the provider did not break down are charged the dearer way. So at these prices the figure meets the charge or sits above it, never below.`}
            >
              About {fmtUsd(ledger.usd)} at list prices
              {ledger.unpriced.length > 0 &&
                ` — and not counting the passes on ${modelNames(ledger.unpriced)}, which ${ledger.unpriced.length === 1 ? 'has' : 'have'} no published rate here`}
              . The tokens above are counted; this is not a bill.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
